import {
  Box,
  Button,
  Text,
  Flex,
  VStack,
  HStack,
  Icon,
  IconButton,
  Image,
  useToast,
  Input,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepNumber,
  StepSeparator,
  StepTitle,
  StepDescription,
  useSteps,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import {
  MdArrowBack,
  MdArrowForward,
  MdAccessTime,
  MdLocationOn,
  MdInfo,
  MdCheckCircle,
  MdAdd,
  MdTimer,
} from "react-icons/md";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import GoogleMapsLoader, { useGoogleMaps } from "components/GoogleMapsLoader";
import LoadingSpinner from "components/LoadingSpinner";
import { BASE_URL } from "config/api";
import { validateEmail, validatePhone, validationMessages } from "utils/validation";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import telephonemarImg from "assets/img/telephonemar.png";
import omnidocImg from "assets/img/dashboards/OMNIDOC.png";
import medecinImg from "assets/img/medecins.png";
import infirmierImg from "assets/img/infirmiers.png";
import { v4 as uuidv4 } from "uuid";
import {
  getAllPatients,
  checkToken,
  getUser,
} from "../../../api/api";
import apiClient from "../../../api/client";
import { fetchVehicleLocation as fetchVehicleLocationSecure, sendSMS } from "../../../api/external";
import { formatErrorForToast } from "../../../utils/errorHandler";

dayjs.extend(utc);
dayjs.extend(timezone);

// Composant dropdown pour la catégorie d'Urgence élevée
const UrgenceEleveeCategoryDropdown = ({ value, onChange, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const categories = [
    { value: "Urgences traumatiques", label: "Urgences traumatiques", color: "#FFF4E6" },
    { value: "Urgences infectieuses", label: "Urgences infectieuses", color: "#FFFBF0" },
    { value: "Urgences endocriniennes / métaboliques", label: "Urgences endocriniennes / métaboliques", color: "#E6F7FF" },
    { value: "Urgences psychiatriques", label: "Urgences psychiatriques", color: "#E6F2FF" },
    { value: "Urgences obstétricales et gynécologiques", label: "Urgences obstétricales et gynécologiques", color: "#F0E6FF" },
    { value: "Urgences pédiatriques", label: "Urgences pédiatriques", color: "#FFE6F0" },
    { value: "Urgences toxiques / intoxications", label: "Urgences toxiques / intoxications", color: "#F5F5F5" },
    { value: "Urgences digestives", label: "Urgences digestives", color: "#F5F5F5" },
  ];

  const handleCategorySelect = (category) => {
    onChange(category);
    onClose();
  };

  return (
    <Menu isOpen={isOpen} onClose={onClose}>
      <MenuButton
        as={Button}
        rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
        w="100%"
        h="42px"
        borderRadius="6px"
        border="1px solid"
        borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
        boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
        bg="white"
        textAlign="left"
        fontWeight="400"
        fontSize="13px"
        color={value ? "#455879" : "#656C7A"}
        onClick={onOpen}
        _hover={{ bg: "white" }}
        _active={{ bg: "white" }}
      >
        {value || "Sélectionner une catégorie"}
      </MenuButton>
      <MenuList
        maxH="400px"
        overflowY="auto"
        p="10px"
        borderRadius="6px"
        boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
      >
        {categories.map((cat) => (
          <MenuItem
            key={cat.value}
            onClick={() => handleCategorySelect(cat.value)}
            bg={cat.color}
            border="1px solid"
            borderColor={value === cat.value ? "rgba(69,156,233,0.2)" : "transparent"}
            borderRadius="6px"
            mb="5px"
            p="14px 20px"
          >
            <Text fontSize="13px" color="#455879">
              {cat.label}
            </Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

// Composant dropdown pour le type d'Urgence élevée (toutes les catégories)
const UrgenceEleveeTypeDropdown = ({ value, onChange, category, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const allOptions = {
    "Urgences traumatiques": [
      {
        category: "A - Traumatismes crâniens (CIM-10 : S00-S09)", items: [
          { value: "Traumatisme crânien léger → S06.0", label: "Traumatisme crânien léger → S06.0" },
          { value: "Traumatisme crânien grave → S06.9", label: "Traumatisme crânien grave → S06.9" },
        ]
      },
      {
        category: "B - Traumatisme thoracique (S20-S29)", items: [
          { value: "Contusion thoracique → S20.2", label: "Contusion thoracique → S20.2" },
          { value: "Fracture de côtes → S22", label: "Fracture de côtes → S22" },
        ]
      },
      {
        category: "C - Traumatisme abdominal (S30-S39)", items: [
          { value: "Contusion abdominale → S30.1", label: "Contusion abdominale → S30.1" },
        ]
      },
      {
        category: "D - Membres (S40-S99)", items: [
          { value: "Fracture membre supérieur / inférieur → S52 / S82", label: "Fracture membre supérieur / inférieur → S52 / S82" },
          { value: "Entorse → S93.4", label: "Entorse → S93.4" },
          { value: "Luxation → S83.0", label: "Luxation → S83.0" },
        ]
      },
    ],
    "Urgences infectieuses": [
      {
        items: [
          { value: "Septicémie → A41.9", label: "Septicémie → A41.9" },
          { value: "Méningite → G00.9", label: "Méningite → G00.9" },
          { value: "Pneumonie sévère → J18.9", label: "Pneumonie sévère → J18.9" },
          { value: "Infection urinaire sévère → N39.0", label: "Infection urinaire sévère → N39.0" },
          { value: "Cellulite / Érysipèle → L03.9", label: "Cellulite / Érysipèle → L03.9" },
        ]
      },
    ],
    "Urgences endocriniennes / métaboliques": [
      {
        items: [
          { value: "Hypoglycémie sévère → E11.641", label: "Hypoglycémie sévère → E11.641" },
          { value: "Hyperglycémie / Acidocétose → E11.1", label: "Hyperglycémie / Acidocétose → E11.1" },
          { value: "Crise thyréotoxique → E05.5", label: "Crise thyréotoxique → E05.5" },
          { value: "Insuffisance surrénale aiguë → E27.1", label: "Insuffisance surrénale aiguë → E27.1" },
        ]
      },
    ],
    "Urgences psychiatriques": [
      {
        items: [
          { value: "Tentative de suicide → Z91.5", label: "Tentative de suicide → Z91.5" },
          { value: "Agitation / Agressivité → F05.1", label: "Agitation / Agressivité → F05.1" },
          { value: "État confusionnel aigu → F05.0", label: "État confusionnel aigu → F05.0" },
          { value: "Crise d'angoisse sévère → F41.0", label: "Crise d'angoisse sévère → F41.0" },
        ]
      },
    ],
    "Urgences obstétricales et gynécologiques": [
      {
        items: [
          { value: "Hémorragie du post-partum → O72", label: "Hémorragie du post-partum → O72" },
          { value: "Grossesse extra-utérine → O00.9", label: "Grossesse extra-utérine → O00.9" },
          { value: "Fausse couche avec hémorragie → O03.9", label: "Fausse couche avec hémorragie → O03.9" },
          { value: "Éclampsie → O15", label: "Éclampsie → O15" },
        ]
      },
    ],
    "Urgences pédiatriques": [
      {
        items: [
          { value: "Détresse respiratoire néonatale → P22.0", label: "Détresse respiratoire néonatale → P22.0" },
          { value: "Convulsions fébriles → R56.00", label: "Convulsions fébriles → R56.00" },
          { value: "Déshydratation sévère → E86.0", label: "Déshydratation sévère → E86.0" },
          { value: "Bronchiolite sévère → J21.9", label: "Bronchiolite sévère → J21.9" },
        ]
      },
    ],
    "Urgences toxiques / intoxications": [
      {
        items: [
          { value: "Intoxication médicamenteuse → T50.9", label: "Intoxication médicamenteuse → T50.9" },
          { value: "Intoxication alcoolique aiguë → T51.0", label: "Intoxication alcoolique aiguë → T51.0" },
          { value: "Intoxication aux produits chimiques → T65.9", label: "Intoxication aux produits chimiques → T65.9" },
          { value: "Morsure / Piqûre venimeuse → T63.9", label: "Morsure / Piqûre venimeuse → T63.9" },
        ]
      },
    ],
    "Urgences digestives": [
      {
        items: [
          { value: "Hémorragie digestive → K92.2", label: "Hémorragie digestive → K92.2" },
          { value: "Occlusion intestinale → K56.6", label: "Occlusion intestinale → K56.6" },
          { value: "Pancréatite aiguë → K85", label: "Pancréatite aiguë → K85" },
          { value: "Appendicite aiguë → K35.9", label: "Appendicite aiguë → K35.9" },
        ]
      },
    ],
  };

  const handleTypeSelect = (type) => {
    onChange(type);
    onClose();
  };

  if (!category || !allOptions[category]) {
    return null;
  }

  const options = allOptions[category];
  const placeholder = `Sélectionner la ${category}`;

  return (
    <Menu isOpen={isOpen} onClose={onClose}>
      <MenuButton
        as={Button}
        rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
        w="100%"
        h="42px"
        borderRadius="6px"
        border="1px solid"
        borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
        boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
        bg="white"
        textAlign="left"
        fontWeight="400"
        fontSize="13px"
        color={value ? "#455879" : "#656C7A"}
        onClick={onOpen}
        _hover={{ bg: "white" }}
        _active={{ bg: "white" }}
      >
        {value || placeholder}
      </MenuButton>
      <MenuList
        maxH="400px"
        overflowY="auto"
        p="10px"
        borderRadius="6px"
        boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
      >
        {options.map((group, idx) => (
          <Box key={idx} mb="10px">
            {group.category && (
              <Text
                fontSize="13px"
                fontWeight="600"
                color="#455879"
                mb="5px"
                px="20px"
                py="10px"
              >
                {group.category}
              </Text>
            )}
            {group.items.map((item) => (
              <MenuItem
                key={item.value}
                onClick={() => handleTypeSelect(item.value)}
                bg={value === item.value ? "#EFF6FF" : "white"}
                border="1px solid"
                borderColor={value === item.value ? "rgba(69,156,233,0.2)" : "transparent"}
                borderRadius="6px"
                mb="5px"
                p="14px 20px"
              >
                <HStack justify="space-between" w="100%">
                  <Text fontSize="13px" color="#455879">
                    {item.label}
                  </Text>
                  {value === item.value && (
                    <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                  )}
                </HStack>
              </MenuItem>
            ))}
          </Box>
        ))}
      </MenuList>
    </Menu>
  );
};

// Composant dropdown pour Non vitale (multi-select)
const NonVitaleDropdown = ({ value, onChange, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const selectedValues = value || [];

  const options = [
    { value: "Céphalées → R51", label: "Céphalées → R51" },
    { value: "Fièvre simple → R50.9", label: "Fièvre simple → R50.9" },
    { value: "Douleurs abdominales → R10", label: "Douleurs abdominales → R10" },
    { value: "Lombalgies → M54.5", label: "Lombalgies → M54.5" },
    { value: "Vertiges → R42", label: "Vertiges → R42" },
    { value: "Vomissements → R11", label: "Vomissements → R11" },
    { value: "Douleur dentaire → K08.8", label: "Douleur dentaire → K08.8" },
  ];

  const handleToggle = (optionValue) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(newValues);
  };

  const displayValue = selectedValues.length > 0
    ? selectedValues.join(", ")
    : "Non vitale";

  return (
    <Box position="relative">
      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
          w="100%"
          h="42px"
          borderRadius="6px"
          border="1px solid"
          borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          textAlign="left"
          fontWeight="400"
          fontSize="13px"
          color={selectedValues.length > 0 ? "#455879" : "#656C7A"}
          onClick={onOpen}
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
        >
          {displayValue}
        </MenuButton>
        <MenuList
          maxH="400px"
          overflowY="auto"
          p="10px"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleToggle(option.value)}
              bg={selectedValues.includes(option.value) ? "#EFF6FF" : "white"}
              border="1px solid"
              borderColor={selectedValues.includes(option.value) ? "rgba(69,156,233,0.2)" : "transparent"}
              borderRadius="6px"
              mb="5px"
              p="14px 20px"
            >
              <HStack justify="space-between" w="100%">
                <Text fontSize="13px" color="#455879">
                  {option.label}
                </Text>
                {selectedValues.includes(option.value) && (
                  <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                )}
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

// Composant dropdown pour Hôpital (id, nom, ville, loc)
const HospitalDropdown = ({ value, onChange, options, placeholder, filterByVille, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const filteredOptions = filterByVille
    ? options.filter((h) => (h.ville || "").trim() === (filterByVille || "").trim())
    : options;

  const handleSelect = (hospital) => {
    onChange(hospital);
    onClose();
  };

  const selectedHospital = options.find((h) => h.id === value?.id || h.id === value);

  return (
    <Box position="relative">
      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
          w="100%"
          h="42px"
          borderRadius="6px"
          border="1px solid"
          borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          textAlign="left"
          fontWeight="400"
          fontSize="13px"
          color={selectedHospital ? "#455879" : "#656C7A"}
          onClick={onOpen}
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
        >
          {selectedHospital ? `${selectedHospital.nom}${selectedHospital.ville ? ` - ${selectedHospital.ville}` : ""}` : (placeholder || "Sélectionner un hôpital")}
        </MenuButton>
        <MenuList
          maxH="400px"
          overflowY="auto"
          p="10px"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
        >
          {filteredOptions.length === 0 ? (
            <Box p="14px" textAlign="center">
              <Text fontSize="13px" color="#656C7A">Aucun hôpital trouvé</Text>
            </Box>
          ) : (
            filteredOptions.map((hospital) => (
              <MenuItem
                key={hospital.id}
                onClick={() => handleSelect(hospital)}
                bg={value?.id === hospital.id || value === hospital.id ? "#EFF6FF" : "white"}
                border="1px solid"
                borderColor={value?.id === hospital.id || value === hospital.id ? "rgba(69,156,233,0.2)" : "transparent"}
                borderRadius="6px"
                mb="5px"
                p="14px 20px"
              >
                <HStack justify="space-between" w="100%">
                  <Text fontSize="13px" color="#455879">
                    {hospital.nom}{hospital.ville ? ` - ${hospital.ville}` : ""}
                  </Text>
                  {(value?.id === hospital.id || value === hospital.id) && (
                    <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                  )}
                </HStack>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

// Composant dropdown pour Service
const ServiceDropdown = ({ value, onChange, options, placeholder, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleServiceSelect = (service) => {
    onChange(service);
    onClose();
  };

  // Gérer le cas où options est un tableau de strings ou d'objets
  const normalizedOptions = options.map((opt) => {
    if (typeof opt === "string") {
      return { id: opt, nom: opt };
    }
    return opt;
  });

  const selectedService = normalizedOptions.find((s) => s.id === value || s.nom === value);

  return (
    <Box position="relative">
      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
          w="100%"
          h="42px"
          borderRadius="6px"
          border="1px solid"
          borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          textAlign="left"
          fontWeight="400"
          fontSize="13px"
          color={value ? "#455879" : "#656C7A"}
          onClick={onOpen}
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
        >
          {selectedService ? selectedService.nom : (placeholder || "Sélectionner un service")}
        </MenuButton>
        <MenuList
          maxH="400px"
          overflowY="auto"
          p="10px"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
        >
          {normalizedOptions.length === 0 ? (
            <Box p="14px" textAlign="center">
              <Text fontSize="13px" color="#656C7A">Aucun service disponible</Text>
            </Box>
          ) : (
            normalizedOptions.map((service) => (
              <MenuItem
                key={service.id}
                onClick={() => handleServiceSelect(service.nom || service.id)}
                bg={value === service.nom || value === service.id ? "#EFF6FF" : "white"}
                border="1px solid"
                borderColor={value === service.nom || value === service.id ? "rgba(69,156,233,0.2)" : "transparent"}
                borderRadius="6px"
                mb="5px"
                p="14px 20px"
              >
                <HStack justify="space-between" w="100%">
                  <Text fontSize="13px" color="#455879">
                    {service.nom}
                  </Text>
                  {(value === service.nom || value === service.id) && (
                    <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                  )}
                </HStack>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

// Composant dropdown pour Ville
const VilleDropdown = ({ value, onChange, options, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleVilleSelect = (ville) => {
    onChange(ville);
    onClose();
  };

  return (
    <Box position="relative">
      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
          w="100%"
          h="42px"
          borderRadius="6px"
          border="1px solid"
          borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          textAlign="left"
          fontWeight="400"
          fontSize="13px"
          color={value ? "#455879" : "#656C7A"}
          onClick={onOpen}
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
        >
          {value || "Sélectionner la ville…"}
        </MenuButton>
        <MenuList
          maxH="400px"
          overflowY="auto"
          p="10px"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
        >
          {options.map((ville) => (
            <MenuItem
              key={ville}
              onClick={() => handleVilleSelect(ville)}
              bg={value === ville ? "#EFF6FF" : "white"}
              border="1px solid"
              borderColor={value === ville ? "rgba(69,156,233,0.2)" : "transparent"}
              borderRadius="6px"
              mb="5px"
              p="14px 20px"
            >
              <HStack justify="space-between" w="100%">
                <Text fontSize="13px" color="#455879">
                  {ville}
                </Text>
                {value === ville && (
                  <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                )}
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

// Composant dropdown pour le type d'urgence vitale
const UrgenceVitaleTypeDropdown = ({ value, onChange, type, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const options = {
    Cardiaque: [
      { value: "Arrêt cardiorespiratoire → I46.9", label: "Arrêt cardiorespiratoire → I46.9" },
      { value: "Oedème aigu du poumon (OAP) → J81", label: "Oedème aigu du poumon (OAP) → J81" },
      { value: "Trouble du rythme grave → I47 / I49", label: "Trouble du rythme grave → I47 / I49" },
      { value: "Douleur thoracique suspecte d'infarctus → I21", label: "Douleur thoracique suspecte d'infarctus → I21" },
    ],
    Neurologique: [
      { value: "Accident vasculaire cérébral (AVC)", label: "Accident vasculaire cérébral (AVC)" },
      { value: "Coma", label: "Coma" },
      { value: "Crise convulsive prolongée", label: "Crise convulsive prolongée" },
      { value: "Traumatisme crânien grave", label: "Traumatisme crânien grave" },
    ],
    Respiratoire: [
      { value: "Détresse respiratoire aiguë", label: "Détresse respiratoire aiguë" },
      { value: "Arrêt respiratoire", label: "Arrêt respiratoire" },
      { value: "Obstruction des voies aériennes", label: "Obstruction des voies aériennes" },
      { value: "Asthme sévère", label: "Asthme sévère" },
      { value: "Pneumonie sévère", label: "Pneumonie sévère" },
    ],
  };

  const currentOptions = options[type] || [];

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    onClose();
  };

  return (
    <Box position="relative">
      <Menu isOpen={isOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
          w="100%"
          h="42px"
          borderRadius="6px"
          border="1px solid"
          borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          textAlign="left"
          fontWeight="400"
          fontSize="13px"
          color={value ? "#455879" : "#656C7A"}
          onClick={onOpen}
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
        >
          {value || `Sélectionner un type ${type ? type.toLowerCase() : ""}`}
        </MenuButton>
        <MenuList
          maxH="400px"
          overflowY="auto"
          p="10px"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
        >
          {currentOptions.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleSelect(option.value)}
              bg={value === option.value ? "#EFF6FF" : "white"}
              border="1px solid"
              borderColor={value === option.value ? "rgba(69,156,233,0.2)" : "transparent"}
              borderRadius="6px"
              mb="5px"
              p="14px 20px"
            >
              <HStack justify="space-between" w="100%">
                <Text fontSize="13px" color="#455879">
                  {option.label}
                </Text>
                {value === option.value && (
                  <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                )}
              </HStack>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

// Composant dropdown pour Docteur
const DoctorDropdown = ({ value, onChange, options, isRequired, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSelect = (doctorId) => {
    onChange(doctorId);
    onClose();
    setSearchQuery("");
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    onOpen();
  };

  const filteredOptions = options.filter((doctor) => {
    const fullName = `${doctor.nom || ""} ${doctor.prenom || ""}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  return (
    <Box position="relative" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <Menu isOpen={isOpen} onClose={onClose} closeOnBlur={false}>
        <MenuButton
          as={Button}
          rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
          w="100%"
          h="42px"
          borderRadius="6px"
          border="1px solid"
          borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          textAlign="left"
          fontWeight="400"
          fontSize="13px"
          color={value ? "#455879" : "#656C7A"}
          onClick={handleOpen}
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
        >
          {value ? (() => {
            const doctor = options.find(d => d.id === value);
            return doctor ? `Dr. ${doctor.nom || ""} ${doctor.prenom || ""}`.trim() : "Sélectionner un docteur";
          })() : "Sélectionner un docteur"}
        </MenuButton>
        <MenuList
          maxH="400px"
          overflowY="auto"
          p="10px"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          zIndex={1000}
        >
          {/* Barre de recherche */}
          <Box mb="10px">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdInfo} color="#656C7A" w="16px" h="16px" />
              </InputLeftElement>
              <Input
                placeholder="Recherche d'une Médecin..."
                value={searchQuery}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearchQuery(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                h="36px"
                borderRadius="6px"
                fontSize="13px"
                border="1px solid"
                borderColor="#E5E5E5"
              />
            </InputGroup>
          </Box>
          {filteredOptions.length === 0 ? (
            <Box p="14px" textAlign="center">
              <Text fontSize="13px" color="#656C7A">
                Aucun docteur trouvé
              </Text>
            </Box>
          ) : (
            filteredOptions.map((doctor) => (
              <MenuItem
                key={doctor.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(doctor.id);
                }}
                bg={value === doctor.id ? "#EFF6FF" : "white"}
                border="1px solid"
                borderColor={value === doctor.id ? "rgba(69,156,233,0.2)" : "transparent"}
                borderRadius="6px"
                mb="5px"
                p="14px 20px"
              >
                <HStack justify="space-between" w="100%">
                  <Text fontSize="13px" color="#455879">
                    {doctor.nom} {doctor.prenom}
                  </Text>
                  {value === doctor.id && (
                    <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                  )}
                </HStack>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

// Composant dropdown pour Infirmier
const NurseDropdown = ({ value, onChange, options, isRequired, isInvalid }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSelect = (nurseId) => {
    onChange(nurseId);
    onClose();
    setSearchQuery("");
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    onOpen();
  };

  const filteredOptions = options.filter((nurse) => {
    const fullName = `${nurse.nom || ""} ${nurse.prenom || ""}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  return (
    <Box position="relative" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <Menu isOpen={isOpen} onClose={onClose} closeOnBlur={false}>
        <MenuButton
          as={Button}
          rightIcon={<Icon as={MdArrowForward} transform={isOpen ? "rotate(-90deg)" : "rotate(90deg)"} />}
          w="100%"
          h="42px"
          borderRadius="6px"
          border="1px solid"
          borderColor={isInvalid ? "#E53E3E" : "#E5E5E5"}
          boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
          bg="white"
          textAlign="left"
          fontWeight="400"
          fontSize="13px"
          color={value ? "#455879" : "#656C7A"}
          onClick={handleOpen}
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
        >
          {value ? (() => {
            const nurse = options.find(n => n.id === value);
            return nurse ? `${nurse.nom || ""} ${nurse.prenom || ""}`.trim() : "Sélectionner un infirmier";
          })() : "Sélectionner un infirmier"}
        </MenuButton>
        <MenuList
          maxH="400px"
          overflowY="auto"
          p="10px"
          borderRadius="6px"
          boxShadow="0px 0px 20px 0px rgba(0,0,0,0.1)"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          zIndex={1000}
        >
          {/* Barre de recherche */}
          <Box mb="10px">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdInfo} color="#656C7A" w="16px" h="16px" />
              </InputLeftElement>
              <Input
                placeholder="Recherche d'une Infirmier..."
                value={searchQuery}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearchQuery(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                h="36px"
                borderRadius="6px"
                fontSize="13px"
                border="1px solid"
                borderColor="#E5E5E5"
              />
            </InputGroup>
          </Box>
          {filteredOptions.length === 0 ? (
            <Box p="14px" textAlign="center">
              <Text fontSize="13px" color="#656C7A">
                Aucun infirmier trouvé
              </Text>
            </Box>
          ) : (
            filteredOptions.map((nurse) => (
              <MenuItem
                key={nurse.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(nurse.id);
                }}
                bg={value === nurse.id ? "#EFF6FF" : "white"}
                border="1px solid"
                borderColor={value === nurse.id ? "rgba(69,156,233,0.2)" : "transparent"}
                borderRadius="6px"
                mb="5px"
                p="14px 20px"
              >
                <HStack justify="space-between" w="100%">
                  <Text fontSize="13px" color="#455879">
                    {nurse.nom} {nurse.prenom}
                  </Text>
                  {value === nurse.id && (
                    <Icon as={MdCheckCircle} w="16px" h="16px" color="#006133" />
                  )}
                </HStack>
              </MenuItem>
            ))
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

// Carte d'affectation pour Consultation à domicile (médecins)
const DoctorAffectationMap = ({
  doctors,
  missionLocation,
  selectedDoctorId,
  onDoctorSelect,
}) => {
  const { loaded } = useGoogleMaps();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialiser la carte
  useEffect(() => {
    if (loaded && mapRef.current && !map && missionLocation?.lat && missionLocation?.lng) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: missionLocation.lat, lng: missionLocation.lng },
        zoom: 12,
        styles: [
          { "elementType": "geometry", "stylers": [{ "color": "#ebe3cd" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#523735" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#c9b2a6" }] },
          { "featureType": "administrative.land_parcel", "elementType": "geometry.stroke", "stylers": [{ "color": "#dcd2be" }] },
          { "featureType": "administrative.land_parcel", "elementType": "labels.text.fill", "stylers": [{ "color": "#ae9e90" }] },
          { "featureType": "landscape.natural", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#93817c" }] },
          { "featureType": "poi.park", "elementType": "geometry.fill", "stylers": [{ "color": "#006133" }] },
          { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#447530" }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#fdfcf8" }] },
          { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#f8c967" }] },
          { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#e9bc62" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry", "stylers": [{ "color": "#e98d58" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry.stroke", "stylers": [{ "color": "#db8555" }] },
          { "featureType": "road.local", "elementType": "labels.text.fill", "stylers": [{ "color": "#806b63" }] },
          { "featureType": "transit.line", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "transit.line", "elementType": "labels.text.fill", "stylers": [{ "color": "#8f7d77" }] },
          { "featureType": "transit.line", "elementType": "labels.text.stroke", "stylers": [{ "color": "#ebe3cd" }] },
          { "featureType": "transit.station", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#b9d3c2" }] },
          { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#92998d" }] }
        ],
      });
      setMap(newMap);
    }
  }, [loaded, missionLocation, map]);

  // Placer les marqueurs (patient + médecins)
  useEffect(() => {
    if (!map || !loaded) return;

    // Nettoyer les anciens marqueurs
    markers.forEach((marker) => marker.setMap(null));
    const newMarkers = [];

    // Marqueur patient (même design que l'ambulance : point rouge Figma)
    if (missionLocation?.lat && missionLocation?.lng) {
      const svgMarker = `
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="13.5" fill="rgba(248,40,90,0.2)" stroke="#F8285A" stroke-width="1"/>
          <circle cx="14" cy="14" r="8.33" fill="#F8285A"/>
          <circle cx="14" cy="14" r="4" fill="white" opacity="0.8"/>
        </svg>
      `;
      const iconUrl = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgMarker);

      const patientMarker = new window.google.maps.Marker({
        position: { lat: missionLocation.lat, lng: missionLocation.lng },
        map,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(28, 28),
          anchor: new window.google.maps.Point(14, 14),
        },
        title: "Patient",
        zIndex: 1000,
      });
      newMarkers.push(patientMarker);
    }

    // Marqueurs médecins
    doctors.forEach((doctor) => {
      if (!doctor.loc) return;
      const [latStr, lngStr] = String(doctor.loc).split(",");
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;

      const isSelected = selectedDoctorId === doctor.id;
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        title: `Dr. ${doctor.nom || ""} ${doctor.prenom || ""}`.trim(),
        // Même icône que sur la carte GPS (OMNIDOC), avec taille différente si sélectionné
        icon: {
          url: medecinImg,
          scaledSize: new window.google.maps.Size(isSelected ? 50 : 40, isSelected ? 50 : 40),
          anchor: new window.google.maps.Point(isSelected ? 25 : 20, isSelected ? 25 : 20),
        },
        zIndex: isSelected ? 1001 : 900,
      });
      marker.addListener("click", () => {
        if (onDoctorSelect) {
          onDoctorSelect(doctor.id);
        }
      });
      newMarkers.push(marker);
    });

    setMarkers(newMarkers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loaded, missionLocation, doctors, selectedDoctorId, onDoctorSelect]);

  const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Préparer la liste des médecins avec distances
  const doctorsWithDistance = React.useMemo(() => {
    if (!missionLocation?.lat || !missionLocation?.lng) return [];
    return doctors
      .map((doctor) => {
        if (!doctor.loc) return null;
        const [latStr, lngStr] = String(doctor.loc).split(",");
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        const distance = calculateDistanceKm(
          missionLocation.lat,
          missionLocation.lng,
          lat,
          lng
        );
        return {
          ...doctor,
          _lat: lat,
          _lng: lng,
          _distance: distance,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a._distance ?? 9999) - (b._distance ?? 9999));
  }, [doctors, missionLocation]);

  const filteredDoctors = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return doctorsWithDistance;
    return doctorsWithDistance.filter((doctor) => {
      const fullName = `${doctor.nom || ""} ${doctor.prenom || ""}`.toLowerCase();
      const specialty = (doctor.specialty || "").toLowerCase();
      return fullName.includes(q) || specialty.includes(q);
    });
  }, [doctorsWithDistance, searchQuery]);

  return (
    <Box position="relative" w="100%" h="calc(100vh - 300px)" minH="600px">
      {/* Carte */}
      <Box
        ref={mapRef}
        w="100%"
        h="100%"
        borderRadius="10px"
        overflow="hidden"
      />

      {/* Panneau latéral liste des médecins */}
      <Box
        position="absolute"
        left="10px"
        top="10px"
        w="401px"
        maxH="calc(100vh - 400px)"
        bg="white"
        border="1px solid"
        borderColor="#E5E5E5"
        borderRadius="12px"
        overflow="hidden"
        zIndex={10}
        display="flex"
        flexDirection="column"
      >
        <Box bg="#FCFCFC" borderBottom="1px solid" borderColor="#E5E5E5" flexShrink={0}>
          <Box px="20px" py="10px">
            <Text fontSize="14px" fontWeight="500" color="#455879" mb="8px">
              Médecin la plus proche
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdInfo} color="#656C7A" w="16px" h="16px" />
              </InputLeftElement>
              <Input
                placeholder="Recherche d'un médecin..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                h="36px"
                borderRadius="6px"
                fontSize="13px"
                border="1px solid"
                borderColor="#E5E5E5"
                pl="34px"
              />
            </InputGroup>
          </Box>
        </Box>

        <Box px="14px" pb="14px" flex="1" overflowY="auto" minH="0">
          <VStack spacing="10px" align="stretch">
            {filteredDoctors.length === 0 ? (
              <Box p="12px">
                <Text fontSize="13px" color="#656C7A">
                  Aucun médecin avec position disponible pour cette ville.
                </Text>
              </Box>
            ) : (
              filteredDoctors.map((doctor) => {
                const isSelected = selectedDoctorId === doctor.id;
                return (
                  <Box
                    key={doctor.id}
                    bg={isSelected ? "#EFF6FF" : "white"}
                    border="1px solid"
                    borderColor={isSelected ? "rgba(69,156,233,0.2)" : "#E5E5E5"}
                    borderRadius="6px"
                    p="10px"
                    cursor="pointer"
                    onClick={() => onDoctorSelect && onDoctorSelect(doctor.id)}
                    _hover={{ borderColor: "#006133" }}
                  >
                    <HStack justify="space-between" align="center" spacing="10px">
                      <HStack spacing="10px" align="center">
                        <Box
                          w="40px"
                          h="40px"
                          borderRadius="50%"
                          overflow="hidden"
                          bg="#E3F2FD"
                          flexShrink={0}
                        >
                          <Image
                            src={doctor.photo || medecinImg}
                            alt={`Dr. ${doctor.nom || ""} ${doctor.prenom || ""}`}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                          />
                        </Box>
                        <VStack align="flex-start" spacing="2px">
                          <Text
                            fontSize="14px"
                            fontWeight="500"
                            color={isSelected ? "#006133" : "#455879"}
                          >
                            Dr. {doctor.nom} {doctor.prenom}
                          </Text>
                          <HStack spacing="6px">
                            {typeof doctor._distance === "number" && (
                              <>
                                <Icon
                                  as={MdLocationOn}
                                  w="14px"
                                  h="14px"
                                  color={isSelected ? "#006133" : "#455879"}
                                />
                                <Text
                                  fontSize="13px"
                                  color={isSelected ? "#006133" : "#455879"}
                                >
                                  {doctor._distance.toFixed(1)} km
                                </Text>
                              </>
                            )}
                            {doctor.specialty && (
                              <Text
                                fontSize="12px"
                                color="#656C7A"
                                noOfLines={1}
                              >
                                • {doctor.specialty}
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                      </HStack>
                      {isSelected && (
                        <Icon
                          as={MdCheckCircle}
                          w="18px"
                          h="18px"
                          color="#006133"
                        />
                      )}
                    </HStack>
                  </Box>
                );
              })
            )}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

// Carte d'affectation pour Acte infirmier (infirmiers)
const NurseAffectationMap = ({
  nurses,
  missionLocation,
  selectedNurseId,
  onNurseSelect,
}) => {
  const { loaded } = useGoogleMaps();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialiser la carte
  useEffect(() => {
    if (loaded && mapRef.current && !map && missionLocation?.lat && missionLocation?.lng) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: missionLocation.lat, lng: missionLocation.lng },
        zoom: 12,
        styles: [
          { "elementType": "geometry", "stylers": [{ "color": "#ebe3cd" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#523735" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#c9b2a6" }] },
          { "featureType": "administrative.land_parcel", "elementType": "geometry.stroke", "stylers": [{ "color": "#dcd2be" }] },
          { "featureType": "administrative.land_parcel", "elementType": "labels.text.fill", "stylers": [{ "color": "#ae9e90" }] },
          { "featureType": "landscape.natural", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#93817c" }] },
          { "featureType": "poi.park", "elementType": "geometry.fill", "stylers": [{ "color": "#006133" }] },
          { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#447530" }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#fdfcf8" }] },
          { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#f8c967" }] },
          { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#e9bc62" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry", "stylers": [{ "color": "#e98d58" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry.stroke", "stylers": [{ "color": "#db8555" }] },
          { "featureType": "road.local", "elementType": "labels.text.fill", "stylers": [{ "color": "#806b63" }] },
          { "featureType": "transit.line", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "transit.line", "elementType": "labels.text.fill", "stylers": [{ "color": "#8f7d77" }] },
          { "featureType": "transit.line", "elementType": "labels.text.stroke", "stylers": [{ "color": "#ebe3cd" }] },
          { "featureType": "transit.station", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#b9d3c2" }] },
          { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#92998d" }] }
        ],
      });
      setMap(newMap);
    }
  }, [loaded, missionLocation, map]);

  // Placer les marqueurs (patient + infirmiers)
  useEffect(() => {
    if (!map || !loaded) return;

    // Nettoyer les anciens marqueurs
    markers.forEach((marker) => marker.setMap(null));
    const newMarkers = [];

    // Marqueur patient (même design que l'ambulance : point rouge Figma)
    if (missionLocation?.lat && missionLocation?.lng) {
      const svgMarker = `
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="13.5" fill="rgba(248,40,90,0.2)" stroke="#F8285A" stroke-width="1"/>
          <circle cx="14" cy="14" r="8.33" fill="#F8285A"/>
          <circle cx="14" cy="14" r="4" fill="white" opacity="0.8"/>
        </svg>
      `;
      const iconUrl = "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgMarker);

      const patientMarker = new window.google.maps.Marker({
        position: { lat: missionLocation.lat, lng: missionLocation.lng },
        map,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(28, 28),
          anchor: new window.google.maps.Point(14, 14),
        },
        title: "Patient",
        zIndex: 1000,
      });
      newMarkers.push(patientMarker);
    }

    // Marqueurs infirmiers
    nurses.forEach((nurse) => {
      if (!nurse.loc) return;
      const [latStr, lngStr] = String(nurse.loc).split(",");
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;

      const isSelected = selectedNurseId === nurse.id;
      const marker = new window.google.maps.Marker({
        position: { lat, lng },
        map,
        title: `${nurse.nom || ""} ${nurse.prenom || ""}`.trim(),
        // Même icône que sur la carte GPS (OMNIDOC), avec taille différente si sélectionné
        icon: {
          url: infirmierImg,
          scaledSize: new window.google.maps.Size(isSelected ? 50 : 40, isSelected ? 50 : 40),
          anchor: new window.google.maps.Point(isSelected ? 25 : 20, isSelected ? 25 : 20),
        },
        zIndex: isSelected ? 1001 : 900,
      });
      marker.addListener("click", () => {
        if (onNurseSelect) {
          onNurseSelect(nurse.id);
        }
      });
      newMarkers.push(marker);
    });

    setMarkers(newMarkers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loaded, missionLocation, nurses, selectedNurseId, onNurseSelect]);

  const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Préparer la liste des infirmiers avec distances
  const nursesWithDistance = React.useMemo(() => {
    if (!missionLocation?.lat || !missionLocation?.lng) return [];
    return nurses
      .map((nurse) => {
        if (!nurse.loc) return null;
        const [latStr, lngStr] = String(nurse.loc).split(",");
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        const distance = calculateDistanceKm(
          missionLocation.lat,
          missionLocation.lng,
          lat,
          lng
        );
        return {
          ...nurse,
          _lat: lat,
          _lng: lng,
          _distance: distance,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a._distance ?? 9999) - (b._distance ?? 9999));
  }, [nurses, missionLocation]);

  const filteredNurses = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return nursesWithDistance;
    return nursesWithDistance.filter((nurse) => {
      const fullName = `${nurse.nom || ""} ${nurse.prenom || ""}`.toLowerCase();
      const specialty = (nurse.specialty || nurse.specialité || "").toLowerCase();
      return fullName.includes(q) || specialty.includes(q);
    });
  }, [nursesWithDistance, searchQuery]);

  return (
    <Box position="relative" w="100%" h="calc(100vh - 300px)" minH="600px">
      {/* Carte */}
      <Box
        ref={mapRef}
        w="100%"
        h="100%"
        borderRadius="10px"
        overflow="hidden"
      />

      {/* Panneau latéral liste des infirmiers */}
      <Box
        position="absolute"
        left="10px"
        top="10px"
        w="401px"
        maxH="calc(100vh - 400px)"
        bg="white"
        border="1px solid"
        borderColor="#E5E5E5"
        borderRadius="12px"
        overflow="hidden"
        zIndex={10}
        display="flex"
        flexDirection="column"
      >
        <Box bg="#FCFCFC" borderBottom="1px solid" borderColor="#E5E5E5" flexShrink={0}>
          <Box px="20px" py="10px">
            <Text fontSize="14px" fontWeight="500" color="#455879" mb="8px">
              Infirmier le plus proche
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdInfo} color="#656C7A" w="16px" h="16px" />
              </InputLeftElement>
              <Input
                placeholder="Recherche d'un infirmier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                h="36px"
                borderRadius="6px"
                fontSize="13px"
                border="1px solid"
                borderColor="#E5E5E5"
                pl="34px"
              />
            </InputGroup>
          </Box>
        </Box>

        <Box px="14px" pb="14px" flex="1" overflowY="auto" minH="0">
          <VStack spacing="10px" align="stretch">
            {filteredNurses.length === 0 ? (
              <Box p="12px">
                <Text fontSize="13px" color="#656C7A">
                  Aucun infirmier avec position disponible pour cette ville.
                </Text>
              </Box>
            ) : (
              filteredNurses.map((nurse) => {
                const isSelected = selectedNurseId === nurse.id;
                return (
                  <Box
                    key={nurse.id}
                    bg={isSelected ? "#EFF6FF" : "white"}
                    border="1px solid"
                    borderColor={isSelected ? "rgba(69,156,233,0.2)" : "#E5E5E5"}
                    borderRadius="6px"
                    p="10px"
                    cursor="pointer"
                    onClick={() => onNurseSelect && onNurseSelect(nurse.id)}
                    _hover={{ borderColor: "#006133" }}
                  >
                    <HStack justify="space-between" align="center" spacing="10px">
                      <HStack spacing="10px" align="center">
                        <Box
                          w="40px"
                          h="40px"
                          borderRadius="50%"
                          overflow="hidden"
                          bg="#E3F2FD"
                          flexShrink={0}
                        >
                          <Image
                            src={nurse.photo || infirmierImg}
                            alt={`${nurse.nom || ""} ${nurse.prenom || ""}`}
                            w="100%"
                            h="100%"
                            objectFit="cover"
                          />
                        </Box>
                        <VStack align="flex-start" spacing="2px">
                          <Text
                            fontSize="14px"
                            fontWeight="500"
                            color={isSelected ? "#006133" : "#455879"}
                          >
                            {nurse.nom} {nurse.prenom}
                          </Text>
                          <HStack spacing="6px">
                            {typeof nurse._distance === "number" && (
                              <>
                                <Icon
                                  as={MdLocationOn}
                                  w="14px"
                                  h="14px"
                                  color={isSelected ? "#006133" : "#455879"}
                                />
                                <Text
                                  fontSize="13px"
                                  color={isSelected ? "#006133" : "#455879"}
                                >
                                  {nurse._distance.toFixed(1)} km
                                </Text>
                              </>
                            )}
                            {(nurse.specialty || nurse.specialité) && (
                              <Text
                                fontSize="12px"
                                color="#656C7A"
                                noOfLines={1}
                              >
                                • {nurse.specialty || nurse.specialité}
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                      </HStack>
                      {isSelected && (
                        <Icon
                          as={MdCheckCircle}
                          w="18px"
                          h="18px"
                          color="#006133"
                        />
                      )}
                    </HStack>
                  </Box>
                );
              })
            )}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

// Composant AffectationMap pour l'étape d'affectation
const AffectationMap = ({
  vehicles,
  missionLocation,
  selectedAmbulanceId,
  onAmbulanceSelect,
  doctors,
  ambulanciers,
  fetchVehicleLocation,
  nurses,
  selectedDoctorId,
  selectedNurseId,
  onDoctorSelect,
  onNurseSelect,
  typeMission,
  typeAmbulance
}) => {
  const { loaded } = useGoogleMaps();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [, setMarkers] = useState([]);
  const [, setCircles] = useState([]);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [ambulancesWithDistance, setAmbulancesWithDistance] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [vehicleLocations, setVehicleLocations] = useState({});
  const distanceMatrixCacheRef = useRef(null);
  const directionsCacheRef = useRef(null);

  // Initialiser la carte
  useEffect(() => {
    if (loaded && mapRef.current && !map && missionLocation?.lat && missionLocation?.lng) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: missionLocation.lat, lng: missionLocation.lng },
        zoom: 13,
        styles: [
          { "elementType": "geometry", "stylers": [{ "color": "#ebe3cd" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#523735" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#c9b2a6" }] },
          { "featureType": "administrative.land_parcel", "elementType": "geometry.stroke", "stylers": [{ "color": "#dcd2be" }] },
          { "featureType": "administrative.land_parcel", "elementType": "labels.text.fill", "stylers": [{ "color": "#ae9e90" }] },
          { "featureType": "landscape.natural", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#93817c" }] },
          { "featureType": "poi.park", "elementType": "geometry.fill", "stylers": [{ "color": "#006133" }] },
          { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#447530" }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#fdfcf8" }] },
          { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#f8c967" }] },
          { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#e9bc62" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry", "stylers": [{ "color": "#e98d58" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry.stroke", "stylers": [{ "color": "#db8555" }] },
          { "featureType": "road.local", "elementType": "labels.text.fill", "stylers": [{ "color": "#806b63" }] },
          { "featureType": "transit.line", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "transit.line", "elementType": "labels.text.fill", "stylers": [{ "color": "#8f7d77" }] },
          { "featureType": "transit.line", "elementType": "labels.text.stroke", "stylers": [{ "color": "#ebe3cd" }] },
          { "featureType": "transit.station", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#b9d3c2" }] },
          { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#92998d" }] }
        ],
      });
      setMap(newMap);
    }
  }, [loaded, missionLocation, map]);

  // Récupérer les positions des véhicules via leur tracker_id
  useEffect(() => {
    const fetchLocations = async () => {
      const locations = {};
      for (const vehicle of vehicles) {
        if (vehicle.tracker_id && !vehicleLocations[vehicle.id]) {
          const location = await fetchVehicleLocation(vehicle.tracker_id);
          if (location) {
            locations[vehicle.id] = location;
          }
        } else if (vehicle.lat && vehicle.lng) {
          locations[vehicle.id] = { lat: vehicle.lat, lng: vehicle.lng };
        }
      }
      if (Object.keys(locations).length > 0) {
        setVehicleLocations(prev => ({ ...prev, ...locations }));
      }
    };

    if (vehicles.length > 0) {
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]); // Retirer fetchVehicleLocation des dépendances pour éviter les boucles infinies

  // Calculer les distances et temps d'arrivée
  useEffect(() => {
    if (!map || !loaded || !vehicles.length || !missionLocation?.lat || Object.keys(vehicleLocations).length === 0) return;

    const service = new window.google.maps.DistanceMatrixService();
    const vehiclesWithLocation = vehicles.filter(v => vehicleLocations[v.id]);
    const origins = vehiclesWithLocation.map(v => vehicleLocations[v.id]);
    const destination = { lat: missionLocation.lat, lng: missionLocation.lng };

    if (origins.length === 0) return;

    // Utiliser une clé unique pour éviter les appels répétés
    const locationsKey = JSON.stringify(origins.map(o => `${o.lat},${o.lng}`).sort());
    const destinationKey = `${destination.lat},${destination.lng}`;
    const cacheKey = `${locationsKey}-${destinationKey}`;

    // Vérifier si on a déjà calculé pour ces positions
    if (distanceMatrixCacheRef.current === cacheKey) return;
    distanceMatrixCacheRef.current = cacheKey;

    service.getDistanceMatrix(
      {
        origins: origins,
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === "OK") {
          const ambulancesWithDist = vehiclesWithLocation
            .map((vehicle, index) => {
              const element = response.rows[index]?.elements[0];
              const distance = element?.distance?.value / 1000; // en km
              const duration = element?.duration?.value / 60; // en minutes

              return {
                ...vehicle,
                lat: vehicleLocations[vehicle.id].lat,
                lng: vehicleLocations[vehicle.id].lng,
                distance: distance ? distance.toFixed(1) : null,
                duration: duration ? Math.round(duration) : null,
              };
            })
            .sort((a, b) => {
              if (!a.distance) return 1;
              if (!b.distance) return -1;
              return parseFloat(a.distance) - parseFloat(b.distance);
            });

          setAmbulancesWithDistance(ambulancesWithDist);
        }
      }
    );
    // Utiliser seulement les valeurs primitives dans les dépendances
    // vehicleLocations est un objet qui change de référence, donc on utilise useMemo pour créer une clé stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loaded, vehicles.length, missionLocation?.lat, missionLocation?.lng, Object.keys(vehicleLocations).length]);

  // Créer les marqueurs sur la carte
  useEffect(() => {
    if (!map || !loaded) return;

    const newMarkers = [];
    const newCircles = [];

    // Marqueur pour l'adresse de mission (rouge avec icône selon design Figma)
    if (missionLocation?.lat && missionLocation?.lng) {
      // Créer un marqueur personnalisé avec SVG selon le design Figma
      // Cercle extérieur: rgba(248,40,90,0.2) avec bordure #F8285A
      // Cercle intérieur: #F8285A avec icône radio au centre
      const svgMarker = `
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="13.5" fill="rgba(248,40,90,0.2)" stroke="#F8285A" stroke-width="1"/>
          <circle cx="14" cy="14" r="8.33" fill="#F8285A"/>
          <circle cx="14" cy="14" r="4" fill="white" opacity="0.8"/>
        </svg>
      `;

      const iconUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarker);

      const missionMarker = new window.google.maps.Marker({
        position: { lat: missionLocation.lat, lng: missionLocation.lng },
        map: map,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(28, 28),
          anchor: new window.google.maps.Point(14, 14),
        },
        title: "Patient",
        zIndex: 1000, // S'assurer que le marqueur patient est au-dessus
      });
      newMarkers.push(missionMarker);
    }

    // Marqueurs pour les ambulances
    ambulancesWithDistance.forEach((ambulance) => {
      const location = vehicleLocations[ambulance.id];
      if (location?.lat && location?.lng) {
        const isSelected = selectedAmbulanceId === ambulance.id;
        if (isSelected) {
          // Marqueur personnalisé pour l'ambulance sélectionnée selon design Figma
          // Utiliser un SVG avec les cercles et l'image en base64 ou URL directe
          const svgMarkerSelected = `
            <svg width="85" height="85" viewBox="0 0 85 85" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="42.5" cy="42.5" r="42.5" fill="rgba(41,186,205,0.2)" stroke="rgba(41,186,205,0.2)" stroke-width="1"/>
              <circle cx="42.5" cy="42.5" r="32.5" fill="#29BACD" stroke="white" stroke-width="2"/>
            </svg>
          `;

          const iconUrlSelected = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarkerSelected);

          // Marqueur avec le cercle de fond
          const circleMarker = new window.google.maps.Marker({
            position: { lat: location.lat, lng: location.lng },
            map: map,
            icon: {
              url: iconUrlSelected,
              scaledSize: new window.google.maps.Size(85, 85),
              anchor: new window.google.maps.Point(42.5, 42.5),
            },
            title: ambulance.immatriculation || `AMB-${ambulance.id}`,
            zIndex: 1000,
          });
          newMarkers.push(circleMarker);

          // Marqueur avec l'image OMNIDOC par-dessus (centrée dans le cercle)
          const imageMarker = new window.google.maps.Marker({
            position: { lat: location.lat, lng: location.lng },
            map: map,
            icon: {
              url: omnidocImg,
              scaledSize: new window.google.maps.Size(50, 50), // Taille plus grande pour meilleure visibilité
              anchor: new window.google.maps.Point(25, 25), // Centré dans le cercle intérieur
            },
            title: ambulance.immatriculation || `AMB-${ambulance.id}`,
            zIndex: 1001,
          });
          newMarkers.push(imageMarker);
        } else {
          // Marqueur normal avec l'image OMNIDOC
          const ambulanceMarker = new window.google.maps.Marker({
            position: { lat: location.lat, lng: location.lng },
            map: map,
            icon: {
              url: omnidocImg,
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 20),
            },
            title: ambulance.immatriculation || `AMB-${ambulance.id}`,
          });
          newMarkers.push(ambulanceMarker);
        }
      }
    });

    setMarkers((prevMarkers) => {
      prevMarkers.forEach((marker) => marker.setMap(null));
      return newMarkers;
    });
    setCircles((prevCircles) => {
      prevCircles.forEach((circle) => circle.setMap(null));
      return newCircles;
    });
  }, [map, loaded, ambulancesWithDistance, missionLocation, selectedAmbulanceId, vehicleLocations]);

  // Tracer le trajet pour l'ambulance sélectionnée (se met à jour automatiquement quand selectedAmbulanceId change)
  useEffect(() => {
    if (!map || !loaded || !selectedAmbulanceId || !missionLocation?.lat) {
      // Nettoyer le renderer si pas de sélection
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
        setDirectionsRenderer(null);
      }
      return;
    }

    const selectedAmbulance = ambulancesWithDistance.find(amb => amb.id === selectedAmbulanceId);
    if (!selectedAmbulance) {
      // Nettoyer si l'ambulance n'est pas trouvée
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
        setDirectionsRenderer(null);
      }
      return;
    }

    const location = vehicleLocations[selectedAmbulance.id];
    if (!location?.lat || !location?.lng) {
      // Nettoyer si la location n'est pas disponible
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
        setDirectionsRenderer(null);
      }
      return;
    }

    // Vérifier si le trajet a déjà été calculé pour cette combinaison
    const routeKey = `${selectedAmbulanceId}-${location.lat},${location.lng}-${missionLocation.lat},${missionLocation.lng}`;
    if (directionsCacheRef.current === routeKey) {
      return; // Ne pas recalculer si c'est le même trajet
    }
    directionsCacheRef.current = routeKey;

    // Nettoyer le renderer précédent avant de créer le nouveau
    if (directionsRenderer) {
      directionsRenderer.setMap(null);
    }

    const directionsService = new window.google.maps.DirectionsService();
    const renderer = new window.google.maps.DirectionsRenderer({
      map: map,
      suppressMarkers: true, // Ne pas afficher les marqueurs de départ/arrivée
      polylineOptions: {
        strokeColor: "#29BACD",
        strokeWeight: 4,
        strokeOpacity: 0.8,
      },
    });

    renderer._lastRouteKey = routeKey; // Stocker la clé pour éviter les recalculs
    setDirectionsRenderer(renderer);

    directionsService.route(
      {
        origin: { lat: location.lat, lng: location.lng },
        destination: { lat: missionLocation.lat, lng: missionLocation.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          renderer.setDirections(result);
        } else {
          // Nettoyer en cas d'erreur
          renderer.setMap(null);
          setDirectionsRenderer(null);
        }
      }
    );

    // Cleanup function
    return () => {
      if (renderer) {
        renderer.setMap(null);
      }
    };
    // Utiliser seulement les valeurs primitives dans les dépendances pour éviter les boucles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loaded, selectedAmbulanceId, missionLocation?.lat, missionLocation?.lng, vehicleLocations[selectedAmbulanceId]?.lat, vehicleLocations[selectedAmbulanceId]?.lng]);

  // Focus sur l'ambulance sélectionnée
  useEffect(() => {
    if (!map || !loaded || !selectedAmbulanceId) return;

    const selectedAmbulance = ambulancesWithDistance.find(amb => amb.id === selectedAmbulanceId);
    if (selectedAmbulance) {
      const location = vehicleLocations[selectedAmbulance.id];
      if (location?.lat && location?.lng) {
        map.setCenter({ lat: location.lat, lng: location.lng });
        map.setZoom(15);
      }
    }
  }, [map, loaded, selectedAmbulanceId, ambulancesWithDistance, vehicleLocations]);

  const getAmbulancierForVehicle = (vehicle) => {
    // Trouver l'ambulancier associé à ce véhicule
    return ambulanciers.find(a => a.id === vehicle.user_id) || null;
  };

  const filteredAmbulances = ambulancesWithDistance.filter((amb) => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return true;

    const immat = (amb.immatriculation || "").toLowerCase();
    const idText = String(amb.id || "");
    const ambulancier = getAmbulancierForVehicle(amb);
    const ambulancierFullName = `${ambulancier?.nom || ""} ${ambulancier?.prenom || ""}`.toLowerCase();

    return (
      immat.includes(q) ||
      idText.includes(q) ||
      ambulancierFullName.includes(q)
    );
  });

  return (
    <Box position="relative" w="100%" h="calc(100vh - 300px)" minH="600px">
      {/* Carte Google Maps */}
      <Box
        ref={mapRef}
        w="100%"
        h="100%"
        borderRadius="10px"
        overflow="hidden"
      />

      {/* Panneau latéral */}
      <Box
        position="absolute"
        left="10px"
        top="10px"
        w="401px"
        maxH="730px"
        bg="white"
        border="1px solid"
        borderColor="#E5E5E5"
        borderRadius="12px"
        overflow="hidden"
        zIndex={10}
      >
        {/* En-tête */}
        <Box
          bg="#FCFCFC"
          borderBottom="1px solid"
          borderColor="#E5E5E5"
          px="20px"
          py="14px"
        >
          <HStack justify="space-between" align="center">
            <Text fontSize="14px" fontWeight="500" color="#455879">
              Ambulance la plus proche
            </Text>
            <IconButton
              icon={<Icon as={MdArrowForward} transform="rotate(45deg)" />}
              size="sm"
              variant="ghost"
              aria-label="Fermer"
            />
          </HStack>
        </Box>

        {/* Barre de recherche */}
        <Box p="14px">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={MdInfo} color="#656C7A" />
            </InputLeftElement>
            <Input
              placeholder="Recherche d'une Ambulance..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              h="36px"
              borderRadius="6px"
              fontSize="13px"
            />
          </InputGroup>
        </Box>

        {/* Liste des ambulances */}
        <Box
          px="14px"
          pb="14px"
          maxH="calc(730px - 150px)"
          overflowY="auto"
        >
          <VStack spacing="10px" align="stretch">
            {filteredAmbulances.map((ambulance, index) => {
              const isSelected = selectedAmbulanceId === ambulance.id;
              const isClosest = index === 0;
              const ambulancier = getAmbulancierForVehicle(ambulance);

              return (
                <Box
                  key={ambulance.id}
                  bg={isSelected ? "#EFF6FF" : "white"}
                  border="1px solid"
                  borderColor={isSelected ? "rgba(69,156,233,0.2)" : "#E5E5E5"}
                  borderRadius="6px"
                  p="12px"
                  cursor="pointer"
                  onClick={() => onAmbulanceSelect(ambulance.id)}
                  boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                  _hover={{ borderColor: "#006133" }}
                >
                  <HStack justify="space-between" mb="5px">
                    <HStack spacing="10px">
                      <VStack align="flex-start" spacing="0">
                        <HStack spacing="5px" flexWrap="wrap">
                          <Text fontSize="14px" fontWeight="500" color={isSelected ? "#006133" : "#455879"}>
                            {ambulance.immatriculation || `AMB-${ambulance.id}`}
                          </Text>
                          {ambulance.type_vehicule && (
                            <Text fontSize="14px" fontWeight="400" color={isSelected ? "#006133" : "#656C7A"}>
                              - {ambulance.type_vehicule}
                            </Text>
                          )}
                          {ambulancier && (
                            <>
                              <Text fontSize="14px" fontWeight="400" color={isSelected ? "#006133" : "#656C7A"}>
                                - {ambulancier.nom} {ambulancier.prenom}
                              </Text>
                              {ambulance.ville && (
                                <Text fontSize="14px" fontWeight="400" color={isSelected ? "#006133" : "#656C7A"}>
                                  - {ambulance.ville}
                                </Text>
                              )}
                              {ambulance.date_achat && (
                                <Text fontSize="12px" fontWeight="400" color={isSelected ? "#006133" : "#656C7A"}>
                                  ({new Date(ambulance.date_achat).getFullYear()})
                                </Text>
                              )}
                            </>
                          )}
                          {isClosest && (
                            <Box
                              w="6px"
                              h="6px"
                              borderRadius="50%"
                              bg="#F8285A"
                              animation="pulse 2s infinite"
                            />
                          )}
                        </HStack>
                      </VStack>
                    </HStack>
                    {isSelected && (
                      <Box
                        bg="#006133"
                        borderRadius="6px"
                        p="9px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={MdCheckCircle} w="16px" h="16px" color="white" />
                      </Box>
                    )}
                  </HStack>

                  <HStack spacing="5px" mt="5px">
                    {ambulance.distance && (
                      <Box
                        border="1px solid"
                        borderColor={isSelected ? "#006133" : "#E5E5E5"}
                        borderRadius="4px"
                        px="8px"
                        py="8px"
                        bg={isSelected ? "transparent" : "#F9F9F9"}
                      >
                        <HStack spacing="5px">
                          <Icon as={MdLocationOn} w="14px" h="14px" color={isSelected ? "#006133" : "#455879"} />
                          <Text fontSize="14px" color={isSelected ? "#006133" : "#455879"}>
                            {ambulance.distance} Km
                          </Text>
                        </HStack>
                      </Box>
                    )}
                    {ambulance.duration && (
                      <Box
                        border="1px solid"
                        borderColor={isSelected ? "#006133" : "#E5E5E5"}
                        borderRadius="4px"
                        px="8px"
                        py="8px"
                        bg={isSelected ? "transparent" : "#F9F9F9"}
                      >
                        <HStack spacing="5px">
                          <Icon as={MdAccessTime} w="14px" h="14px" color={isSelected ? "#006133" : "#455879"} />
                          <Text fontSize="14px" color={isSelected ? "#006133" : "#455879"}>
                            {ambulance.duration} min
                          </Text>
                        </HStack>
                      </Box>
                    )}
                  </HStack>

                  {/* Dropdowns Infirmier et Docteur */}
                  {isSelected && (
                    <VStack spacing="10px" mt="10px" align="stretch">
                      {/* Dropdown Infirmier en premier */}
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      >
                        <FormLabel fontSize="12px" fontWeight="500" color="#455879" mb="5px">
                          Infirmier
                        </FormLabel>
                        <NurseDropdown
                          value={selectedNurseId}
                          onChange={onNurseSelect}
                          options={nurses}
                          isRequired={false}
                        />
                      </Box>

                      {/* Dropdown Docteur ensuite */}
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      >
                        <FormLabel fontSize="12px" fontWeight="500" color="#455879" mb="5px">
                          Docteur
                        </FormLabel>
                        <DoctorDropdown
                          value={selectedDoctorId}
                          onChange={onDoctorSelect}
                          options={doctors}
                          isRequired={false}
                        />
                      </Box>
                    </VStack>
                  )}
                </Box>
              );
            })}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};



const MissionCreateContent = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { loaded } = useGoogleMaps();

  const [patients, setPatients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [nurses, setNurses] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalVillesList, setHospitalVillesList] = useState([]); // Villes depuis GET /hopitals/villes
  const [medecinsHopitalMission, setMedecinsHopitalMission] = useState([]);
  const [medecinsHopitalDestination, setMedecinsHopitalDestination] = useState([]);
  const [servicesHopitalMission, setServicesHopitalMission] = useState([]); // GET /services/hospital/:id (hôpital mission)
  const [servicesHopitalDestination, setServicesHopitalDestination] = useState([]); // GET /services/hospital/:id (hôpital destination)
  const [uniqueVilles, setUniqueVilles] = useState([]);
  const [allAmbulanciers, setAllAmbulanciers] = useState([]);
  const [availableAmbulanciers, setAvailableAmbulanciers] = useState([]);
  const [missionLocations, setMissionLocations] = useState({});
  const [invalidFields, setInvalidFields] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [createdMissionId, setCreatedMissionId] = useState(null);
  const [partialMissionId, setPartialMissionId] = useState(null);
  const [createdMissionData, setCreatedMissionData] = useState(null);

  const [isAddDoctorModalOpen, setIsAddDoctorModalOpen] = useState(false);
  const [addDoctorContext, setAddDoctorContext] = useState(null); // "mission" ou "destination"
  const [newDoctorData, setNewDoctorData] = useState({
    nom: "",
    prenom: "",
    téléphone: "",
    ville: "",
  });
  const [creatingDoctor, setCreatingDoctor] = useState(false);
  const [isRefreshingEquipe, setIsRefreshingEquipe] = useState(false);

  const [newMissionData, setNewMissionData] = useState({
    type_mission: "ambulance", // Toujours ambulance pour cette version
    cause: "",
    date_mission: dayjs()
      .tz("Africa/Casablanca")
      .format("YYYY-MM-DDTHH:mm"),
    adresse: "", // Nom de l'hôpital mission (rempli par Hôpital / Adresse mission)
    adresse_destination: "", // Nom de l'hôpital destination (rempli par Hôpital destination)
    demande_materiel: "",
    patient_id: "",
    doctor_id: "",
    ambulance_id: "",
    statut: "créée",
    ville: "",
    type_ambulance: "TAR",
    ambulancier_id: "",
    nurse_id: "",
    etat_mission: "urgente",
    urgence: true,
    gravite: "",
    urgence_vitale: "",
    urgence_vitale_type: "",
    urgence_elevee: "",
    urgence_elevee_type: "",
    non_vitale: [],
    ta: "",
    fc: "",
    spo2: "",
    glasgow: "",
    autre: "",
    diagnostic: "",
    motif: "",
    add_new_patient: false,
    perimetre: "",
    perimetre_urbain: false,
    perimetre_rayon50: false,
    loc: "",
    hospital_id: "", // Hôpital sélectionné (adresse mission)
    hospital_destination_id: "", // Hôpital destination (ambulance)
    ville_dest: "", // Ville destination (appliquée sur hôpital destination) -> DB: ville_dest
    doc_a: "", // Médecin traitant (hôpital mission) - GET users/hopital/:id/medecin
    doc_b: "", // Médecin destinataire (hôpital destination)
    service_a: "", // Service de l'hôpital mission
    service_b: "", // Service de l'hôpital destination
    accord_destination: "oui", // Toujours "oui"
    timer: 15, // Timer initial de 15 secondes
    heure_mission: "", // Heure séparée pour Acte infirmier
    acte_type: "", // Type d'acte : "Actes techniques", "Actes de surveillance médicale", "Actes de prélèvements"
    acte_technique: "", // Acte technique sélectionné
    acte_surveillance: "", // Acte de surveillance médicale sélectionné
    acte_prelevement: "", // Acte de prélèvement sélectionné
    new_patient: {
      nom: "",
      prenom: "",
      age: "",
      sexe: "",
      téléphone: "",
      cin: "",
      ipp: "",
      adresse: "",
      ville: "",
    },
  });

  const isTwoStepFlow = useMemo(() => {
    const etat = (newMissionData.etat_mission || "").toLowerCase();
    const isEtatMatch =
      etat === "planifiée" ||
      etat === "planifiee" ||
      etat === "urgente" ||
      etat === "urgent";
    const isGraviteMatch =
      newMissionData.gravite === "Urgence élevée" ||
      newMissionData.gravite === "Non vitale";
    return isEtatMatch && isGraviteMatch;
  }, [newMissionData.etat_mission, newMissionData.gravite]);

  const shouldShowFullPatientInStep1 = useMemo(() => {
    const etat = (newMissionData.etat_mission || "").toLowerCase();
    return (
      isTwoStepFlow ||
      etat === "planifiée" ||
      etat === "planifiee" ||
      etat === "aller-retour"
    );
  }, [isTwoStepFlow, newMissionData.etat_mission]);

  // Stepper dynamique (2 ou 3 étapes)
  const steps = useMemo(() => {
    if (isTwoStepFlow) {
      return [
        { title: "Informations de mission", description: "Détails initiaux" },
        { title: "Affectation", description: "Choisissez l'équipe assignée" },
      ];
    }
    return [
      { title: "Informations de mission", description: "Détails initiaux" },
      { title: "Affectation", description: "Choisissez l'équipe assignée" },
      { title: "Patient", description: "Infos patient" },
    ];
  }, [isTwoStepFlow]);

  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });
  const lastStepIndex = steps.length - 1;

  useEffect(() => {
    if (activeStep > lastStepIndex) {
      setActiveStep(lastStepIndex);
    }
  }, [activeStep, lastStepIndex, setActiveStep]);

  const fetchPatients = useCallback(async () => {
    try {
      const res = await getAllPatients();
      // Support pour la nouvelle structure paginée et l'ancienne structure
      let patientsData = [];
      if (Array.isArray(res.data?.data)) {
        patientsData = res.data.data;
      } else if (Array.isArray(res.data)) {
        patientsData = res.data;
      }
      setPatients(patientsData);
    } catch (err) {
      toast({
        title: "Erreur lors du chargement des patients",
        status: "error",
        isClosable: true,
      });
    }
  }, [toast]);


  const fetchVehicles = useCallback(async () => {
    try {
      const response = await apiClient.get(`${BASE_URL}/vehicules`);
      setVehicles(response.data);
      const villes = [...new Set(response.data.map((vehicle) => vehicle.ville))];
      setUniqueVilles(villes);
    } catch (err) {
      toast({
        title: "Erreur lors du chargement des véhicules",
        status: "error",
        isClosable: true,
      });
    }
  }, [toast]);

  const fetchHopitaux = useCallback(async () => {
    try {
      const res = await apiClient.get(`${BASE_URL}/hopitals`);
      setHospitals(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast({
        title: "Erreur lors du chargement des hôpitaux",
        status: "error",
        isClosable: true,
      });
    }
  }, [toast]);

  const fetchHospitalVilles = useCallback(async () => {
    try {
      const res = await apiClient.get(`${BASE_URL}/hopitals/villes`);
      const data = res.data;
      const list = Array.isArray(data) ? data : (data?.villes ? data.villes : []);
      setHospitalVillesList(Array.isArray(list) ? list.filter((v) => v != null && String(v).trim() !== "").sort() : []);
    } catch (err) {
      setHospitalVillesList([]);
    }
  }, []);

  const fetchMedecinsByHopital = async (hospitalId) => {
    if (!hospitalId) return [];
    try {
      const res = await apiClient.get(`${BASE_URL}/users/hopital/${hospitalId}/medecins`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      return [];
    }
  };

  const fetchServicesByHopital = useCallback(async (hospitalId) => {
    if (!hospitalId) return [];
    try {
      const res = await apiClient.get(`${BASE_URL}/services/hospital/${hospitalId}`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      return [];
    }
  }, []);

  const fetchDoctors = useCallback(async () => {
    try {
      const res = await apiClient.get(`${BASE_URL}/users`);
      const doctorsList = res.data.filter((user) => user.rôle === "médecin");
      setDoctors(doctorsList);
    } catch (err) {
      const errorToast = formatErrorForToast(err);
      toast(errorToast);
    }
  }, [toast]);

  // Fonction pour récupérer la position d'un véhicule (utilise la configuration sécurisée)
  const fetchVehicleLocation = async (trackerId) => {
    try {
      return await fetchVehicleLocationSecure(trackerId);
    } catch (error) {
      return null;
    }
  };

  const fetchNurses = useCallback(async () => {
    try {
      const res = await apiClient.get(`${BASE_URL}/users`);

      const normalize = (value) => {
        if (!value || typeof value !== "string") return "";
        return value
          .normalize("NFD")
          .replace(/\p{Diacritic}/gu, "")
          .toLowerCase();
      };

      const nursesList = res.data.filter((user) => {
        const roleRaw = user.rôle ?? user.role ?? user["rÃ´le"];
        const role = normalize(roleRaw);
        return (
          role === "infirmier" || role === "infirmiere" || role === "nurse"
        );
      });

      setNurses(nursesList);
    } catch (err) {
      toast({
        title: "Erreur lors du chargement des infirmiers",
        status: "error",
        isClosable: true,
      });
    }
  }, [toast]);

  const fetchAllAmbulanciers = useCallback(async () => {
    try {
      const res = await apiClient.get(`${BASE_URL}/users`);
      const list = Array.isArray(res.data)
        ? res.data.filter((user) => user.rôle === "ambulancier")
        : [];
      setAllAmbulanciers(list);
      return list;
    } catch (err) {
      // Ne pas vider la liste existante (évite de perdre les noms affichés)
      return allAmbulanciers;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAmbulanciers = async (typeAmbulance, snapshot) => {
    try {
      const [usersRes, vehiclesRes] = await Promise.all([
        apiClient.get(`${BASE_URL}/users`),
        apiClient.get(`${BASE_URL}/vehicules`),
      ]);

      const ambulanciers = usersRes.data.filter(
        (user) => user.rôle === "ambulancier"
      );
      const currentVille = snapshot?.ville ?? newMissionData.ville;
      const currentEtatMission = snapshot?.etat_mission ?? newMissionData.etat_mission;

      const filteredVehicles = vehiclesRes.data.filter((vehicle) => {
        const isMatchingType =
          typeAmbulance === "TAS"
            ? vehicle.type_vehicule === "TAS" ||
            vehicle.type_vehicule === "TAR"
            : typeAmbulance === "VSL"
              ? ["VSL", "TAS", "TAR"].includes(vehicle.type_vehicule)
              : vehicle.type_vehicule === typeAmbulance;

        return (
          isMatchingType &&
          vehicle.ville === currentVille &&
          (currentEtatMission === "planifiée" ||
            currentEtatMission === "Aller-Retour" ||
            vehicle.statut === "disponible")
        );
      });

      const availableAmbulancierIds = [
        ...new Set(filteredVehicles.map((vehicle) => vehicle.user_id)),
      ];
      const availableAmbulanciers = ambulanciers.filter((ambulancier) =>
        availableAmbulancierIds.includes(ambulancier.id)
      );

      // Toujours garder une liste "stable" pour l'affichage des noms (tous les ambulanciers)
      setAllAmbulanciers(ambulanciers);
      setAvailableAmbulanciers(availableAmbulanciers);
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les ambulanciers",
        status: "error",
        isClosable: true,
      });
    }
  };

  const handleRefreshEquipe = useCallback(async () => {
    if (isRefreshingEquipe) return;
    try {
      setIsRefreshingEquipe(true);

      // Recharger de manière atomique les véhicules + utilisateurs
      // pour refléter immédiatement les changements ambulance <-> ambulancier.
      const [vehiclesRes, usersRes] = await Promise.all([
        apiClient.get(`${BASE_URL}/vehicules`),
        apiClient.get(`${BASE_URL}/users`),
      ]);

      const freshVehicles = Array.isArray(vehiclesRes.data) ? vehiclesRes.data : [];
      const freshUsers = Array.isArray(usersRes.data) ? usersRes.data : [];

      const normalize = (value) => {
        if (!value || typeof value !== "string") return "";
        return value
          .normalize("NFD")
          .replace(/\p{Diacritic}/gu, "")
          .toLowerCase();
      };

      const freshAmbulanciers = freshUsers.filter((user) => user.rôle === "ambulancier");
      const freshDoctors = freshUsers.filter((user) => user.rôle === "médecin");
      const freshNurses = freshUsers.filter((user) => {
        const roleRaw = user.rôle ?? user.role ?? user["rÃ´le"];
        const role = normalize(roleRaw);
        return role === "infirmier" || role === "infirmiere" || role === "nurse";
      });

      setVehicles(freshVehicles);
      setUniqueVilles([...new Set(freshVehicles.map((vehicle) => vehicle.ville))]);
      setAllAmbulanciers(freshAmbulanciers);
      setDoctors(freshDoctors);
      setNurses(freshNurses);

      // Recharger la liste des ambulanciers dispo (dépend du type_ambulance)
      if (newMissionData.type_mission === "ambulance" && newMissionData.type_ambulance) {
        await fetchAmbulanciers(newMissionData.type_ambulance, {
          ville: newMissionData.ville,
          etat_mission: newMissionData.etat_mission,
        });
      }

      // Si une ambulance est déjà sélectionnée, resynchroniser son ambulancier actuel
      // (cas: un ambulancier change de véhicule entre-temps).
      setNewMissionData((prev) => {
        if (!prev.ambulance_id) return prev;
        const selectedVehicle = freshVehicles.find(
          (vehicle) => String(vehicle.id) === String(prev.ambulance_id)
        );
        if (!selectedVehicle) return prev;
        return {
          ...prev,
          ambulancier_id: selectedVehicle.user_id || "",
        };
      });

      toast({
        title: "Équipe rafraîchie",
        description: "Les données d'affectation ont été mises à jour.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Erreur",
        description: "Impossible de rafraîchir les données d'équipe.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsRefreshingEquipe(false);
    }
  }, [
    isRefreshingEquipe,
    newMissionData.etat_mission,
    newMissionData.type_ambulance,
    newMissionData.type_mission,
    newMissionData.ville,
    toast,
  ]);

  // Parser loc "lat,lng" en { lat, lng }
  const parseLocString = (loc) => {
    if (!loc || typeof loc !== "string") return null;
    const parts = String(loc).trim().split(",");
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const openAddDoctorModal = (context) => {
    setAddDoctorContext(context); // "mission" ou "destination"
    setNewDoctorData({
      nom: "",
      prenom: "",
      téléphone: "",
      ville: context === "mission" ? (newMissionData.ville || "") : (newMissionData.ville_dest || ""),
    });
    setIsAddDoctorModalOpen(true);
  };

  const handleCreateDoctor = async () => {
    if (!addDoctorContext) return;

    const targetHospitalId =
      addDoctorContext === "mission"
        ? newMissionData.hospital_id
        : newMissionData.hospital_destination_id;

    if (!targetHospitalId) {
      toast({
        title: "Hôpital requis",
        description: "Veuillez d'abord sélectionner un hôpital avant d'ajouter un médecin.",
        status: "error",
        isClosable: true,
      });
      return;
    }

    if (!newDoctorData.nom.trim() || !newDoctorData.prenom.trim()) {
      toast({
        title: "Champs obligatoires manquants",
        description: "Nom et prénom du médecin sont obligatoires.",
        status: "error",
        isClosable: true,
      });
      return;
    }

    try {
      setCreatingDoctor(true);

      const baseEmail = (newDoctorData.nom + newDoctorData.prenom)
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .replace(/\s+/g, "")
        .toLowerCase();
      const email = `${baseEmail || "medecin"}@rsk.ma`;

      const basePrenom = (newDoctorData.prenom || "")
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .replace(/\s+/g, "")
        .toLowerCase();
      const motDePasse = `${basePrenom || "medecin"}@rsk2026`;

      const payload = {
        nom: newDoctorData.nom.trim(),
        prenom: newDoctorData.prenom.trim(),
        téléphone: newDoctorData.téléphone || null,
        ville: newDoctorData.ville || null,
        email,
        mot_de_passe: motDePasse,
        rôle: "médecin",
        statut: "actif",
        date_naissance: dayjs().format("YYYY-MM-DD"),
        langue: "fr",
        hospital_id: targetHospitalId,
      };

      const res = await apiClient.post(`${BASE_URL}/users`, payload);
      const created = res.data;

      const updatedList = await fetchMedecinsByHopital(targetHospitalId);
      if (addDoctorContext === "mission") {
        setMedecinsHopitalMission(updatedList);
        if (created?.id) {
          setNewMissionData((prev) => ({ ...prev, doc_a: created.id }));
        }
      } else {
        setMedecinsHopitalDestination(updatedList);
        if (created?.id) {
          setNewMissionData((prev) => ({ ...prev, doc_b: created.id }));
        }
      }

      toast({
        title: "Médecin créé",
        description: "Le médecin a été ajouté avec succès.",
        status: "success",
        isClosable: true,
      });

      setIsAddDoctorModalOpen(false);
      setAddDoctorContext(null);
    } catch (error) {
      console.error("Erreur lors de la création du médecin:", error);
      toast({
        title: "Erreur",
        description: "Impossible de créer le médecin.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setCreatingDoctor(false);
    }
  };

  const handleHospitalSelect = (hospital) => {
    if (!hospital) return;
    const locObj = parseLocString(hospital.loc);
    setMissionLocations((prev) => ({
      ...prev,
      new: locObj || prev.new,
    }));

    // Reset errors
    if (invalidFields.hospital_id || invalidFields.hospital_id_a || invalidFields.ville) {
      setInvalidFields(prev => {
        const newErrors = { ...prev };
        delete newErrors.hospital_id;
        delete newErrors.ville;
        return newErrors;
      });
    }

    setNewMissionData((prev) => ({
      ...prev,
      hospital_id: hospital.id,
      adresse: hospital.nom || prev.adresse,
      ville: hospital.ville || prev.ville,
      loc: hospital.loc || prev.loc,
      ambulance_id: "",
      doc_a: "", // Réinitialiser médecin traitant quand on change d'hôpital
      service_a: "", // Réinitialiser service quand on change d'hôpital
    }));
  };

  const handleHospitalDestinationSelect = (hospital) => {
    if (!hospital) return;
    const locObj = parseLocString(hospital.loc);
    setMissionLocations((prev) => ({
      ...prev,
      destination: locObj || prev.destination,
    }));

    // Reset errors
    if (invalidFields.hospital_destination_id || invalidFields.ville_dest) {
      setInvalidFields(prev => {
        const newErrors = { ...prev };
        delete newErrors.hospital_destination_id;
        delete newErrors.ville_dest;
        return newErrors;
      });
    }

    setNewMissionData((prev) => ({
      ...prev,
      hospital_destination_id: hospital.id,
      adresse_destination: hospital.nom || prev.adresse_destination,
      ville_dest: hospital.ville || prev.ville_dest, // Ville destination = ville de l'hôpital destination
      doc_b: "", // Réinitialiser médecin destinataire quand on change d'hôpital
      service_b: "", // Réinitialiser service quand on change d'hôpital
      accord_destination: "oui", // Toujours "oui"
    }));
  };

  // Charger les médecins de l'hôpital mission (pour doc_a)
  useEffect(() => {
    if (!newMissionData.hospital_id) {
      setMedecinsHopitalMission([]);
      return;
    }
    let cancelled = false;
    fetchMedecinsByHopital(newMissionData.hospital_id).then((list) => {
      if (!cancelled) setMedecinsHopitalMission(list);
    });
    return () => { cancelled = true; };
  }, [newMissionData.hospital_id]);

  // Charger les services de l'hôpital mission (GET /services/hospital/:id)
  useEffect(() => {
    if (!newMissionData.hospital_id) {
      setServicesHopitalMission([]);
      return;
    }
    let cancelled = false;
    fetchServicesByHopital(newMissionData.hospital_id).then((list) => {
      if (!cancelled) setServicesHopitalMission(list);
    });
    return () => { cancelled = true; };
  }, [newMissionData.hospital_id, fetchServicesByHopital]);

  // Charger les médecins de l'hôpital destination (pour doc_b)
  useEffect(() => {
    if (!newMissionData.hospital_destination_id) {
      setMedecinsHopitalDestination([]);
      return;
    }
    let cancelled = false;
    fetchMedecinsByHopital(newMissionData.hospital_destination_id).then((list) => {
      if (!cancelled) setMedecinsHopitalDestination(list);
    });
    return () => { cancelled = true; };
  }, [newMissionData.hospital_destination_id]);

  // Charger les services de l'hôpital destination (GET /services/hospital/:id)
  useEffect(() => {
    if (!newMissionData.hospital_destination_id) {
      setServicesHopitalDestination([]);
      return;
    }
    let cancelled = false;
    fetchServicesByHopital(newMissionData.hospital_destination_id).then((list) => {
      if (!cancelled) setServicesHopitalDestination(list);
    });
    return () => { cancelled = true; };
  }, [newMissionData.hospital_destination_id, fetchServicesByHopital]);

  // Normaliser le sexe pour l'affichage (M, F, male, female → Homme, Femme)
  const normalizeSexeForDisplay = (sexe) => {
    if (!sexe || typeof sexe !== "string") return "";
    const s = sexe.trim().toLowerCase();
    if (s === "m" || s === "male" || s === "homme" || s === "h") return "Homme";
    if (s === "f" || s === "female" || s === "femme") return "Femme";
    return sexe; // garder tel quel si déjà Homme/Femme
  };

  const handlePatientFieldChange = (field, value) => {
    const normalizedValue =
      field === "téléphone" && typeof value === "string"
        ? value.replace(/[^0-9]/g, "").slice(0, 10)
        : value;
    setNewMissionData((prev) => {
      const updatedPatient = { ...prev.new_patient, [field]: normalizedValue };
      const cinTrim = (updatedPatient.cin || "").trim();
      const ippTrim = (updatedPatient.ipp || "").trim();
      const foundByCin = cinTrim ? patients.find((p) => (p.cin || "").trim() === cinTrim) : null;
      const foundByIpp = ippTrim
        ? patients.find((p) => String(p.ipp ?? "").trim() === ippTrim)
        : null;

      // Réinitialiser l'erreur pour ce champ
      const fieldKey = `new_patient_${field}`;
      if (invalidFields[fieldKey]) {
        setInvalidFields(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[fieldKey];
          return newErrors;
        });
      }

      // IPP : même logique que le CIN — chargement du patient existant si trouvé
      if (field === "ipp" && foundByIpp) {
        const sexeRaw = foundByIpp.sexe ?? "";
        const sexeNormalized = sexeRaw ? normalizeSexeForDisplay(sexeRaw) || sexeRaw : "";
        return {
          ...prev,
          new_patient: {
            nom: foundByIpp.nom ?? "",
            prenom: foundByIpp.prenom ?? "",
            age: foundByIpp.age ?? "",
            sexe: sexeNormalized,
            téléphone: foundByIpp.téléphone ?? "",
            cin: foundByIpp.cin ?? "",
            ipp: foundByIpp.ipp != null ? String(foundByIpp.ipp) : ippTrim,
            adresse: foundByIpp.adresse ?? "",
            ville: foundByIpp.ville ?? "",
          },
          patient_id: foundByIpp.id,
        };
      }

      // Ne remplir depuis l'API que lorsque c'est le CIN qui change : permet de modifier nom, prénom, âge, sexe, IPP ensuite
      if (field === "cin" && foundByCin) {
        const sexeRaw = foundByCin.sexe ?? "";
        const sexeNormalized = sexeRaw ? normalizeSexeForDisplay(sexeRaw) || sexeRaw : "";
        return {
          ...prev,
          new_patient: {
            nom: foundByCin.nom ?? "",
            prenom: foundByCin.prenom ?? "",
            age: foundByCin.age ?? "",
            sexe: sexeNormalized,
            téléphone: foundByCin.téléphone ?? "",
            cin: foundByCin.cin ?? cinTrim,
            ipp: foundByCin.ipp != null ? String(foundByCin.ipp) : "",
            adresse: foundByCin.adresse ?? "",
            ville: foundByCin.ville ?? "",
          },
          patient_id: foundByCin.id,
        };
      }

      // Sans CIN ni IPP : recherche par nom + prénom + téléphone
      const found = !cinTrim && !ippTrim
        ? patients.find(
          (p) =>
            (p.nom || "").trim().toLowerCase() === (updatedPatient.nom || "").trim().toLowerCase() &&
            (p.prenom || "").trim().toLowerCase() === (updatedPatient.prenom || "").trim().toLowerCase() &&
            (p.téléphone || "").trim() === (updatedPatient.téléphone || "").trim()
        )
        : null;
      return {
        ...prev,
        new_patient: updatedPatient,
        patient_id: found
          ? found.id
          : foundByCin
            ? foundByCin.id
            : foundByIpp
              ? foundByIpp.id
              : prev.patient_id,
      };
    });
  };



  // Fonction pour générer automatiquement la valeur de "cause"
  const generateCause = (missionData) => {
    if (!missionData.gravite) return "";

    if (missionData.gravite === "Urgence vitale") {
      if (missionData.urgence_vitale && missionData.urgence_vitale_type) {
        // Construire la cause avec "Urgence vitale" + le type (Cardiaque, Neurologique, Respiratoire)
        return `${missionData.gravite} ${missionData.urgence_vitale} -> ${missionData.urgence_vitale_type}`;
      }
      if (missionData.urgence_vitale) {
        // Si seulement le type est sélectionné sans sous-type
        return `${missionData.gravite} ${missionData.urgence_vitale}`;
      }
      return missionData.gravite;
    }

    if (missionData.gravite === "Urgence élevée") {
      if (missionData.urgence_elevee && missionData.urgence_elevee_type) {
        return `${missionData.gravite} : ${missionData.urgence_elevee} -> ${missionData.urgence_elevee_type}`;
      }
      if (missionData.urgence_elevee) {
        return `${missionData.gravite} : ${missionData.urgence_elevee}`;
      }
      return missionData.gravite;
    }

    if (missionData.gravite === "Non vitale") {
      if (missionData.non_vitale && missionData.non_vitale.length > 0) {
        return `${missionData.gravite} : ${missionData.non_vitale.join(", ")}`;
      }
      return missionData.gravite;
    }

    return missionData.gravite;
  };

  const validateAllFields = () => {
    const newInvalidFields = {};
    const isAmbulancePlanifiee = newMissionData.type_mission === "ambulance" &&
      (newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour");
    const requiredFields = [
      { key: "type_mission", label: "Type de Mission" },
      {
        key: "adresse",
        label: "Adresse de Mission",
        condition: newMissionData.type_mission !== "Téléconsultation",
      },
      {
        key: "ville",
        label: "Ville mission",
        condition: newMissionData.type_mission !== "Téléconsultation",
      },
      { key: "etat_mission", label: "État de Mission" },
      {
        key: "date_mission",
        label: "Date de Mission",
        condition: newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour",
      },
      {
        key: "cause",
        label: "Cause",
        condition: !isAmbulancePlanifiee && newMissionData.type_mission !== "Acte infirmier", // Cause non obligatoire pour missions ambulance planifiées et gérée différemment pour Acte infirmier
      },
      // Pour Acte infirmier, vérifier qu'un acte spécifique est sélectionné
      {
        key: "acte_technique",
        label: "Actes techniques",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes techniques",
      },
      {
        key: "acte_surveillance",
        label: "Actes de surveillance médicale",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes de surveillance médicale",
      },
      {
        key: "acte_prelevement",
        label: "Actes de prélèvements",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes de prélèvements",
      },
      { key: "new_patient.ipp", label: "IPP du patient" },
      { key: "new_patient.nom", label: "Nom du patient" },
      { key: "new_patient.prenom", label: "Prénom du patient" },
      {
        key: "type_ambulance",
        label: "Type d'Ambulance",
        condition: newMissionData.type_mission === "ambulance",
      },
      {
        key: "ambulancier_id",
        label: "Ambulancier",
        condition: newMissionData.type_mission === "ambulance",
      },
      {
        key: "nurse_id",
        label: "Infirmier",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "doc_a",
        label: "Médecin demandeur",
        condition: newMissionData.type_mission !== "Téléconsultation" && newMissionData.hospital_id,
      },
      {
        key: "service_a",
        label: "Service",
        condition: newMissionData.type_mission !== "Téléconsultation" && newMissionData.hospital_id,
      },
      {
        key: "ville_dest",
        label: "Ville destination",
        condition: newMissionData.type_mission === "ambulance",
      },
      {
        key: "hospital_destination_id",
        label: "Hôpital destination",
        condition: newMissionData.type_mission === "ambulance",
      },
      {
        key: "service_b",
        label: "Service destination",
        condition: newMissionData.type_mission === "ambulance" && newMissionData.hospital_destination_id,
      },
      {
        key: "doc_b",
        label: "Médecin destinataire",
        condition: newMissionData.type_mission === "ambulance",
      },
    ];

    let firstInvalid = null;
    for (const field of requiredFields) {
      if (field.condition === false) continue;
      let value = newMissionData;
      let fieldKey = field.key;
      if (field.key.includes(".")) {
        const [parent, child] = field.key.split(".");
        value = value[parent]?.[child];
        fieldKey = `${parent}_${child}`;
      } else {
        value = value[field.key];
      }
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newInvalidFields[fieldKey] = `Le champ "${field.label}" est obligatoire.`;
        if (!firstInvalid) firstInvalid = field.label;
      }
    }

    // Validation spécifique pour email et téléphone du patient
    if (newMissionData.new_patient?.téléphone && !validatePhone(newMissionData.new_patient.téléphone)) {
      newInvalidFields["new_patient_téléphone"] = validationMessages.phone.invalid;
      if (!firstInvalid) firstInvalid = "Téléphone du patient";
    }

    if (newMissionData.new_patient?.email && !validateEmail(newMissionData.new_patient.email)) {
      newInvalidFields["new_patient_email"] = validationMessages.email.invalid;
      if (!firstInvalid) firstInvalid = "Email du patient";
    }

    setInvalidFields(newInvalidFields);
    if (Object.keys(newInvalidFields).length > 0) {
      toast({
        title: "Champs obligatoires manquants",
        description: `Veuillez remplir tous les champs obligatoires. Premier champ manquant : ${firstInvalid}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  // === Validations par étape pour le Stepper ===
  const validateStep1 = () => {
    const newInvalidFields = {};
    const isAmbulancePlanifiee = newMissionData.type_mission === "ambulance" &&
      (newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour");
    const requiredFields = [
      { key: "etat_mission", label: "État de Mission" },
      {
        key: "date_mission",
        label: "Date de Mission",
        condition: newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour",
      },
      {
        key: "gravite",
        label: "Gravité",
        condition: !isAmbulancePlanifiee && newMissionData.type_mission !== "Acte infirmier", // Gravité non obligatoire pour missions ambulance planifiées et Acte infirmier
      },
      // Champs spécifiques pour Acte infirmier
      {
        key: "acte_type",
        label: "Actes de surveillance médicale",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "acte_technique",
        label: "Actes techniques",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes techniques",
      },
      {
        key: "acte_surveillance",
        label: "Actes de surveillance médicale",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes de surveillance médicale",
      },
      {
        key: "acte_prelevement",
        label: "Actes de prélèvements",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes de prélèvements",
      },
      {
        key: "heure_mission",
        label: "L'heure de Mission",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "ville",
        label: "Ville mission",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "adresse",
        label: "Adresse mission",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "perimetre",
        label: "Périmètre",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "urgence_vitale",
        label: "Urgence vitale",
        condition: !isAmbulancePlanifiee && newMissionData.gravite === "Urgence vitale",
      },
      // Urgence élevée : la catégorie (Urgences traumatiques, etc.) est facultative
      {
        key: "non_vitale",
        label: "Non vitale",
        condition: !isAmbulancePlanifiee && newMissionData.gravite === "Non vitale",
      },
      {
        key: "new_patient.ipp",
        label: "IPP du patient",
      },
      {
        key: "ville",
        label: "Ville mission",
        condition: newMissionData.type_mission !== "Téléconsultation",
      },
      {
        key: "adresse",
        label: "Adresse de Mission",
        condition: newMissionData.type_mission !== "Téléconsultation",
      },
      {
        key: "type_ambulance",
        label: "Mode de transport",
        condition: newMissionData.type_mission === "ambulance",
      },
      // Champs spécifiques pour Acte infirmier
      {
        key: "acte_type",
        label: "Actes de surveillance médicale",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "acte_technique",
        label: "Actes techniques",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes techniques",
      },
      {
        key: "acte_surveillance",
        label: "Actes de surveillance médicale",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes de surveillance médicale",
      },
      {
        key: "acte_prelevement",
        label: "Actes de prélèvements",
        condition: newMissionData.type_mission === "Acte infirmier" && newMissionData.acte_type === "Actes de prélèvements",
      },
      {
        key: "date_mission",
        label: "Date de Mission",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "heure_mission",
        label: "L'heure de Mission",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "ville",
        label: "Ville mission",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "adresse",
        label: "Adresse mission",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "perimetre",
        label: "Périmètre",
        condition: newMissionData.type_mission === "Acte infirmier",
      },
      {
        key: "doc_a",
        label: "Médecin demandeur",
        condition: newMissionData.type_mission !== "Téléconsultation" && newMissionData.hospital_id,
      },
      {
        key: "service_a",
        label: "Service",
        condition: newMissionData.type_mission !== "Téléconsultation" && newMissionData.hospital_id,
      },
      {
        key: "ville_dest",
        label: "Ville destination",
        condition: newMissionData.type_mission === "ambulance",
      },
      {
        key: "hospital_destination_id",
        label: "Hôpital destination",
        condition: newMissionData.type_mission === "ambulance",
      },
      {
        key: "service_b",
        label: "Service destination",
        condition: newMissionData.type_mission === "ambulance" && newMissionData.hospital_destination_id,
      },
      {
        key: "doc_b",
        label: "Médecin destinataire",
        condition: newMissionData.type_mission === "ambulance",
      },
    ];

    let firstInvalid = null;
    requiredFields.forEach((field) => {
      if (field.condition === false) return;
      let value = newMissionData;
      let fieldKey = field.key;
      if (field.key.includes(".")) {
        const [parent, child] = field.key.split(".");
        value = value[parent]?.[child];
        fieldKey = `${parent}_${child}`;
      } else {
        value = value[field.key];
      }
      // Gestion spéciale pour les tableaux (non_vitale)
      if (Array.isArray(value)) {
        if (value.length === 0) {
          newInvalidFields[fieldKey] = `Le champ "${field.label}" est obligatoire.`;
          if (!firstInvalid) firstInvalid = field.label;
        }
      } else if (
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newInvalidFields[fieldKey] = `Le champ "${field.label}" est obligatoire.`;
        if (!firstInvalid) firstInvalid = field.label;
      }
    });

    // Validation du format du téléphone du patient
    const phoneValue = newMissionData.new_patient?.téléphone;
    if (phoneValue && !validatePhone(phoneValue)) {
      newInvalidFields["new_patient_téléphone"] = validationMessages.phone.invalid;
      if (!firstInvalid) firstInvalid = "Téléphone du patient";
      toast({
        title: "Numéro de téléphone invalide",
        description: validationMessages.phone.invalid,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setInvalidFields((prev) => ({ ...prev, ...newInvalidFields }));
      return false;
    }

    if (Object.keys(newInvalidFields).length > 0) {
      setInvalidFields((prev) => ({ ...prev, ...newInvalidFields }));
      toast({
        title: "Champs obligatoires manquants",
        description: `Veuillez remplir tous les champs obligatoires. Premier champ manquant : ${firstInvalid}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const newInvalidFields = {};
    const requiredFields = [
      { key: "new_patient.nom", label: "Nom" },
      { key: "new_patient.prenom", label: "Prénom" },
      { key: "new_patient.age", label: "Âge" },
      { key: "new_patient.sexe", label: "Sexe" },
      { key: "perimetre", label: "Périmètre" },
    ];

    let firstInvalid = null;
    requiredFields.forEach((field) => {
      // Si une condition est définie, on vérifie si elle est vraie
      // Si la condition est false, null, undefined, ou 0, on ignore ce champ
      if (field.condition !== undefined) {
        // Convertir explicitement en booléen pour éviter les problèmes avec null/undefined
        const conditionResult = Boolean(field.condition);
        if (!conditionResult) {
          return; // Ignorer ce champ si la condition est false
        }
      }
      // Si une condition est définie et qu'elle est true, on valide le champ
      // Si aucune condition n'est définie (undefined), on valide le champ (champ toujours requis)
      let value = newMissionData;
      let fieldKey = field.key;
      if (field.key.includes(".")) {
        const [parent, child] = field.key.split(".");
        value = value[parent]?.[child];
        fieldKey = `${parent}_${child}`;
      } else {
        value = value[field.key];
      }
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (typeof value === "string" && value.trim() === "")
      ) {
        newInvalidFields[fieldKey] = `Le champ "${field.label}" est obligatoire.`;
        if (!firstInvalid) firstInvalid = field.label;
      }
    });

    if (Object.keys(newInvalidFields).length > 0) {
      setInvalidFields((prev) => ({ ...prev, ...newInvalidFields }));
      toast({
        title: "Champs obligatoires manquants",
        description: `Veuillez remplir tous les champs obligatoires. Premier champ manquant : ${firstInvalid}`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };



  // Fonction pour créer la mission partiellement (sans patient/client)
  const createPartialMission = async (missionData) => {
    try {
      const currentUser = await getUser();

      // Pour les missions ambulance : réutiliser le patient existant (trouvé par IPP / CIN) ou créer un patient minimal uniquement si aucun patient_id
      let patientId = missionData.patient_id ? (typeof missionData.patient_id === "number" ? missionData.patient_id : parseInt(missionData.patient_id, 10)) : null;
      if (Number.isNaN(patientId)) patientId = null;

      const ipp = (missionData.new_patient?.ipp || "").trim();
      const phone = (missionData.new_patient?.téléphone || "").trim();
      // Patient minimal : identifiant principal = IPP (plus CIN à l'étape patient)
      if (missionData.type_mission === "ambulance" && (ipp || phone) && !patientId) {
        const minimalPatientData = {
          nom: null,
          prenom: null,
          cin: null,
          ipp: ipp || null,
          téléphone: phone || null,
          adresse: null,
          ville: null,
        };

        try {
          const newPatientRes = await apiClient.post(`${BASE_URL}/patients`, minimalPatientData);
          patientId = newPatientRes.data.id;
        } catch (patientErr) {
          // Continuer même si la création du patient échoue
        }
      }

      if (missionData.etat_mission !== "planifiée" && missionData.etat_mission !== "Aller-Retour" && missionData.ambulancier_id) {
        const vehiclesRes = await apiClient.get(`${BASE_URL}/vehicules`);

        const vehicle = vehiclesRes.data.find(
          (v) => v.user_id === missionData.ambulancier_id
        );
        if (vehicle) {
          await apiClient.put(`/vehicules/${vehicle.id}`, {
            ...vehicle,
            statut: "en mission",
          });
        }
      }

      let resolvedAmbulanceId = missionData.ambulance_id || null;
      if (!resolvedAmbulanceId && missionData.ambulancier_id) {
        try {
          const localVehicle = vehicles.find(
            (v) => v.user_id === missionData.ambulancier_id
          );
          if (localVehicle) {
            resolvedAmbulanceId = localVehicle.id;
          } else {
            const vehiclesRes2 = await apiClient.get(`${BASE_URL}/vehicules`);
            const found = vehiclesRes2.data.find(
              (v) => v.user_id === missionData.ambulancier_id
            );
            if (found) {
              resolvedAmbulanceId = found.id;
            }
          }
        } catch (e) {
        }
      }

      // Générer la cause si elle n'est pas déjà définie
      const generatedCause = generateCause(missionData);

      const partialMissionData = {
        patient_id: patientId,
        etat_mission: missionData.etat_mission || null,
        statut: missionData.statut || "créée",
        date_mission: missionData.date_mission || null,
        urgence: missionData.urgence !== undefined ? missionData.urgence : true,
        type_mission: missionData.type_mission || null,
        cause: generatedCause || missionData.cause || null,
        demande_materiel: missionData.demande_materiel || null,
        ambulancier_id: missionData.ambulancier_id ? parseInt(missionData.ambulancier_id) : null,
        ambulance_id: resolvedAmbulanceId ? parseInt(resolvedAmbulanceId) : null,
        type_ambulance: missionData.type_ambulance || null,
        adresse: missionData.adresse || null, // Nom de l'hôpital mission
        adresse_destination: missionData.adresse_destination || null, // Nom de l'hôpital destination
        hospital_id: missionData.hospital_id ? parseInt(missionData.hospital_id) : null,
        hospital_destination_id: missionData.hospital_destination_id ? parseInt(missionData.hospital_destination_id) : null,
        phone: 1, // Booléen toujours à 1 pour cette page
        doctor_id: missionData.doctor_id ? parseInt(missionData.doctor_id) : null,
        nurse_id: missionData.nurse_id ? parseInt(missionData.nurse_id) : null,
        ville: missionData.ville || null,
        ville_dest: missionData.ville_dest || null,
        perimetre: missionData.perimetre || null,
        loc: missionData.loc || null,
        loc_dest: (() => {
          const dest = missionLocations["destination"];
          if (dest && typeof dest.lat === "number" && typeof dest.lng === "number") return `${dest.lat},${dest.lng}`;
          return null;
        })(),
        doc_a: missionData.doc_a ? parseInt(missionData.doc_a) : null,
        doc_b: missionData.doc_b ? parseInt(missionData.doc_b) : null,
        service_a: missionData.service_a || null,
        service_b: missionData.service_b || null,
        ta: missionData.ta || null,
        fc: missionData.fc || null,
        spo2: missionData.spo2 || null,
        glasgow: missionData.glasgow || null,
        autre: missionData.autre || null,
        diagnostic: missionData.diagnostic || null,
        motif: missionData.motif || null,
        accord: "oui", // Toujours "oui"
        créé_par: currentUser?.id ?? null,
        timer: missionData.timer !== undefined ? Math.max(0, 15 - (missionData.timer || 15)) : null,
      };

      delete partialMissionData.add_new_patient;
      delete partialMissionData.new_patient;

      const response = await apiClient.post(`${BASE_URL}/missions`, partialMissionData);
      if (response?.data?.id === null) {
        console.warn("[Mission Create Debug] createPartialMission - Réponse sans id, structure complète:", response);
      }

      const missionId = response.data.id;

      // Stocker les coordonnées pour la nouvelle mission partielle
      if (missionLocations["new"]) {
        setMissionLocations((prev) => ({
          ...prev,
          [missionId]: missionLocations["new"],
          new: null,
        }));
      }

      // Gérer loc (coordonnées de la mission)
      let loc = missionData.loc || null;
      const locObj = missionLocations["new"];
      if (locObj && typeof locObj.lat === "number" && typeof locObj.lng === "number") {
        loc = `${locObj.lat},${locObj.lng}`;
      }

      // Définir finalPatientId pour l'utiliser dans les notifications et SMS
      const finalPatientId = patientId;

      // Create notifications for each user involved in the mission
      const notifications = [];

      try {
        // Get user details for the message
        const usersRes = await apiClient.get(`${BASE_URL}/users`);

        // Get patient details
        const patient = patients.find((p) => p.id === finalPatientId) ||
          (missionData.new_patient ? {
            nom: missionData.new_patient.nom || 'N/A',
            prenom: missionData.new_patient.prenom || 'N/A',
            téléphone: missionData.new_patient.téléphone || 'N/A'
          } : null);

        // Create notification for ambulancier if exists
        if (missionData.ambulancier_id) {
          const doctor = missionData.doctor_id ? usersRes.data.find((u) => u.id === missionData.doctor_id) : null;
          const nurse = missionData?.nurse_id ? usersRes.data.find((u) => u.id === missionData.nurse_id) : null;

          const message = `Nouvelle mission:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'}
Demande Matériel: ${missionData.demande_materiel || 'N/A'}

Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}

Médecin:
${doctor?.nom || 'N/A'} ${doctor?.prenom || 'N/A'}
Téléphone: ${doctor?.téléphone || 'N/A'}

Infirmier:
${nurse?.nom || 'N/A'} ${nurse?.prenom || 'N/A'}
Téléphone: ${nurse?.téléphone || 'N/A'}`;

          notifications.push({
            user_id: missionData.ambulancier_id,
            mission_id: missionId,
            message: message,
            date_notification: missionData.date_mission || new Date().toISOString(),
            type: "mission",
            statut: "nonlue"
          });
        }

        // Create notification for doctor if exists
        if (missionData.doctor_id) {
          const ambulancier = missionData.ambulancier_id ? usersRes.data.find((u) => u.id === missionData.ambulancier_id) : null;
          const nurse = missionData?.nurse_id ? usersRes.data.find((u) => u.id === missionData.nurse_id) : null;

          const message = `Nouvelle mission:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'} 
Demande Matériel: ${missionData.demande_materiel || 'N/A'}

Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}

Ambulancier:
${ambulancier?.nom || 'N/A'} ${ambulancier?.prenom || 'N/A'}
Téléphone: ${ambulancier?.téléphone || 'N/A'}

Infirmier:
${nurse?.nom || 'N/A'} ${nurse?.prenom || 'N/A'}
Téléphone: ${nurse?.téléphone || 'N/A'}`;

          notifications.push({
            user_id: missionData.doctor_id,
            mission_id: missionId,
            message: message,
            date_notification: missionData.date_mission || new Date().toISOString(),
            type: "mission",
            statut: "nonlue"
          });
        }

        // Create notification for nurse if exists
        if (missionData?.nurse_id) {
          const nurse = usersRes.data.find((u) => u.id === missionData.nurse_id);
          const ambulancier = missionData.ambulancier_id ? usersRes.data.find((u) => u.id === missionData.ambulancier_id) : null;
          const doctor = missionData.doctor_id ? usersRes.data.find((u) => u.id === missionData.doctor_id) : null;

          if (nurse) {
            const message = missionData.type_mission === "Acte infirmier"
              ? `Nouvelle mission d'acte infirmier:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'}
Demande Matériel: ${missionData.demande_materiel || 'N/A'}

Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}

Médecin:
${doctor?.nom || 'N/A'} ${doctor?.prenom || 'N/A'}
Téléphone: ${doctor?.téléphone || 'N/A'}

Ambulancier:
${ambulancier?.nom || 'N/A'} ${ambulancier?.prenom || 'N/A'}
Téléphone: ${ambulancier?.téléphone || 'N/A'}`
              : `Nouvelle mission:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'}
Demande Matériel: ${missionData.demande_materiel || 'N/A'}
                  
Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}
                  
Ambulancier:
${ambulancier?.nom || 'N/A'} ${ambulancier?.prenom || 'N/A'}
Téléphone: ${ambulancier?.téléphone || 'N/A'}
                  
Médecin:
${doctor?.nom || 'N/A'} ${doctor?.prenom || 'N/A'}
Téléphone: ${doctor?.téléphone || 'N/A'}`;

            notifications.push({
              user_id: missionData.nurse_id,
              mission_id: missionId,
              message: message,
              date_notification: missionData.date_mission || new Date().toISOString(),
              type: "mission",
              statut: "nonlue"
            });
          }
        }

        // Create all notifications
        for (const notification of notifications) {
          try {
            await apiClient.post(`${BASE_URL}/notifications`, notification);
          } catch (err) {
          }
        }
      } catch (notifErr) {
        // Ne pas bloquer la création de mission si les notifications échouent
      }

      // SMS + map pour tous les types de missions
      if (missionData.type_mission === "ambulance") {
        const vehicle = vehicles.find((v) => v.user_id === missionData.ambulancier_id);
        const trackerId = vehicle?.tracker_id || null;

        const id = uuidv4();
        const map = {
          id,
          missionid: missionId,
          loc,
          tracker_id: trackerId,
          fin: 0,
        };

        try {
          await apiClient.post(`${BASE_URL}/map`, map);

          const phone =
            patients.find((p) => p.id === finalPatientId)?.téléphone ||
            missionData.new_patient?.téléphone;

          if (phone && map.id) {
            // use env url
            const message = `Omnidoc Santé\nVotre ambulance est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
            try {
              await sendSMS(phone, message);
            } catch (smsErr) {
            }
          }
        } catch (err) {
        }
      }
      // Cas Consultation à domicile -> map avec userid = doctor_id
      else if (missionData.type_mission === "Consultation à domicile" && missionData.doctor_id) {
        const id = uuidv4();
        const map = {
          id: id,
          missionid: missionId,
          loc: loc,
          userid: parseInt(missionData.doctor_id),
          fin: 0
        };

        try {
          await apiClient.post(`${BASE_URL}/map`, map);

          const phone =
            patients.find((p) => p.id === finalPatientId)?.téléphone ||
            missionData.new_patient?.téléphone;

          if (phone && map.id) {
            const message = `Omnidoc Santé\nVotre médecin est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
            try {
              await sendSMS(phone, message);
            } catch (smsErr) {
            }
          }
        } catch (err) {
        }
      }
      // Cas Acte infirmier -> map avec userid = nurse_id
      else if (missionData.type_mission === "Acte infirmier" && missionData.nurse_id) {
        const id = uuidv4();
        const map = {
          id: id,
          missionid: missionId,
          loc: loc,
          userid: parseInt(missionData.nurse_id),
          fin: 0
        };

        try {
          await apiClient.post(`${BASE_URL}/map`, map);

          const phone =
            patients.find((p) => p.id === finalPatientId)?.téléphone ||
            missionData.new_patient?.téléphone;

          if (phone && map.id) {
            const message = `Omnidoc Santé\nVotre infirmier est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
            try {
              await sendSMS(phone, message);
            } catch (smsErr) {
            }
          }
        } catch (err) {
        }
      }

      toast({
        title: "Mission créée partiellement",
        description: "Vous pouvez maintenant compléter les informations client et patient",
        status: "success",
        isClosable: true,
      });

      // Lier l'appel en attente à la mission créée si un appel est en attente
      try {
        const pendingCallStr = localStorage.getItem('pending_call_for_mission');
        if (pendingCallStr) {
          const pendingCall = JSON.parse(pendingCallStr);

          if (pendingCall.uniqueid) {
            // Utiliser le chemin audio depuis pendingCall, ou le récupérer depuis localStorage
            let audioPath = pendingCall.audioPath || null;

            if (!audioPath) {
              try {
                const audioInfoStr = localStorage.getItem(`call_audio_${pendingCall.uniqueid}`);
                if (audioInfoStr) {
                  const audioInfo = JSON.parse(audioInfoStr);
                  audioPath = audioInfo.audioPath;
                }
              } catch (e) {
                console.warn('Impossible de récupérer le chemin audio:', e);
              }
            }

            // Si toujours pas de chemin, construire directement (appel peut être encore en cours)
            if (!audioPath && pendingCall.uniqueid) {
              // Format: /uploads/DD-MM-YYYY/uniqueid.webm
              const today = dayjs();
              const dateStr = today.format('DD-MM-YYYY');
              audioPath = `/uploads/${dateStr}/${pendingCall.uniqueid}.webm`;
            }

            // Lier l'appel à la mission créée
            const linkPayload = {
              missionid: response.data.id,
              callid: pendingCall.uniqueid, // Utiliser l'uniqueid comme callid
              lien: audioPath, // Chemin du fichier audio
            };

            const linkResponse = await apiClient.post('/missioncalls', linkPayload);

            if (linkResponse.data.success || linkResponse.status === 200 || linkResponse.status === 201) {
              // Supprimer l'appel en attente
              localStorage.removeItem('pending_call_for_mission');
              toast({
                title: 'Appel lié',
                description: `L'appel a été lié à la mission #${response.data.id}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }
          }
        }
      } catch (linkErr) {
        // Ne pas bloquer la création de mission si la liaison échoue
        console.error('❌ Erreur liaison appel-mission:', linkErr);
        console.error('❌ Détails erreur:', linkErr.response?.data || linkErr.message);
        toast({
          title: 'Avertissement',
          description: 'La mission a été créée mais la liaison avec l\'appel a échoué. Vous pourrez la lier manuellement.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

      return response.data;
    } catch (err) {
      // Debug: erreur création partielle
      console.error("[Mission Create Debug] createPartialMission - Erreur:", err);
      console.error("[Mission Create Debug] createPartialMission - err.response:", err?.response);
      console.error("[Mission Create Debug] createPartialMission - err.response?.data:", err?.response?.data);
      console.error("[Mission Create Debug] createPartialMission - err.response?.status:", err?.response?.status);
      toast({
        title: "Erreur lors de la création de la mission",
        status: "error",
        isClosable: true,
      });
      throw err;
    }
  };

  // Fonction pour mettre à jour la mission avec les données client/patient
  const updateMission = async (missionId, missionData, patientId) => {
    try {
      // Récupérer d'abord les données existantes de la mission
      const existingMissionRes = await apiClient.get(`/missions/${missionId}`);
      const existingMission = existingMissionRes.data;

      // Utiliser le patient_id de la mission existante si patientId n'est pas fourni
      const existingPatientId = patientId || existingMission.patient_id;

      // Mettre à jour le patient existant avec les nom/prénom et autres infos
      if (existingPatientId && missionData.new_patient) {
        // Mise à jour du patient : inclure l'IPP pour ne pas le supprimer (certaines API remplacent tout le document)
        const updatePatientData = {
          ipp: missionData.new_patient.ipp ?? null,
          nom: missionData.new_patient.nom || null,
          prenom: missionData.new_patient.prenom || null,
          age: missionData.new_patient.age || null,
          sexe: missionData.new_patient.sexe || null,
          téléphone: missionData.new_patient.téléphone || null,
          cin: missionData.new_patient.cin || null,
          adresse: missionData.new_patient.adresse || null,
          ville: missionData.new_patient.ville || null,
        };

        try {
          await apiClient.put(`/patients/${existingPatientId}`, updatePatientData);
        } catch (updateErr) {
          // Si la mise à jour échoue, on continue quand même avec la mission
          console.warn("Erreur lors de la mise à jour du patient:", updateErr);
        }
        patientId = existingPatientId; // Utiliser le patient_id mis à jour
      }

      // Fusionner les données existantes avec les nouvelles données du formulaire (noms hôpitaux dans adresse/adresse_destination, IDs dans hospital_id/hospital_destination_id)
      const updateData = {
        ...existingMission,
        patient_id: patientId || existingMission.patient_id,
        ville: missionData.ville ?? existingMission.ville,
        ville_dest: missionData.ville_dest ?? existingMission.ville_dest,
        adresse: missionData.adresse ?? existingMission.adresse, // Nom de l'hôpital mission
        adresse_destination: missionData.adresse_destination ?? existingMission.adresse_destination, // Nom de l'hôpital destination
        loc: missionData.loc ?? existingMission.loc,
        doc_a: missionData.doc_a ? parseInt(missionData.doc_a) : (existingMission.doc_a ?? null),
        doc_b: missionData.doc_b ? parseInt(missionData.doc_b) : (existingMission.doc_b ?? null),
        service_a: missionData.service_a ?? existingMission.service_a ?? null,
        service_b: missionData.service_b ?? existingMission.service_b ?? null,
        ta: missionData.ta ?? existingMission.ta ?? null,
        fc: missionData.fc ?? existingMission.fc ?? null,
        spo2: missionData.spo2 ?? existingMission.spo2 ?? null,
        glasgow: missionData.glasgow ?? existingMission.glasgow ?? null,
        autre: missionData.autre ?? existingMission.autre ?? null,
        diagnostic: missionData.diagnostic ?? existingMission.diagnostic ?? null,
        motif: missionData.motif ?? existingMission.motif ?? null,
        accord: "oui", // Toujours "oui"
      };
      if (missionData.hospital_id != null) {
        updateData.hospital_id = missionData.hospital_id ? parseInt(missionData.hospital_id) : null;
      }
      if (missionData.hospital_destination_id != null) {
        updateData.hospital_destination_id = missionData.hospital_destination_id ? parseInt(missionData.hospital_destination_id) : null;
      }

      // Debug: payload mise à jour
      const response = await apiClient.put(`/missions/${missionId}`, updateData);

      toast({
        title: "Mission mise à jour avec succès",
        status: "success",
        isClosable: true,
      });

      // Retourner les données avec l'ID de la mission
      return { ...response.data, id: missionId };
    } catch (err) {
      console.error("[Mission Create Debug] updateMission - Erreur:", err);
      console.error("[Mission Create Debug] updateMission - err.response?.data:", err?.response?.data);
      console.error("[Mission Create Debug] updateMission - err.response?.status:", err?.response?.status);
      toast({
        title: "Erreur lors de la mise à jour de la mission",
        status: "error",
        isClosable: true,
      });
      throw err;
    }
  };

  const createMission = async (missionData, patientId) => {
    try {
      const currentUser = await getUser();

      // Mettre à jour le patient existant avec les nom/prénom et autres infos
      if (patientId && missionData.new_patient) {
        try {
          // Mise à jour du patient : inclure l'IPP pour ne pas le supprimer (certaines API remplacent tout le document)
          const updatePatientData = {
            ipp: missionData.new_patient.ipp ?? null,
            nom: missionData.new_patient.nom || null,
            prenom: missionData.new_patient.prenom || null,
            age: missionData.new_patient.age || null,
            sexe: missionData.new_patient.sexe || null,
            téléphone: missionData.new_patient.téléphone || null,
            cin: missionData.new_patient.cin || null,
            adresse: missionData.new_patient.adresse || null,
            ville: missionData.new_patient.ville || null,
          };
          await apiClient.put(`/patients/${patientId}`, updatePatientData);
        } catch (patientErr) {
          // Continuer même si la mise à jour du patient échoue
          console.warn("Erreur lors de la mise à jour du patient:", patientErr);
        }
      }

      if (missionData.etat_mission !== "planifiée" && missionData.etat_mission !== "Aller-Retour" && missionData.ambulancier_id) {
        const vehiclesRes = await apiClient.get(`${BASE_URL}/vehicules`);

        const vehicle = vehiclesRes.data.find(
          (v) => v.user_id === missionData.ambulancier_id
        );
        if (vehicle) {
          await apiClient.put(`/vehicules/${vehicle.id}`, {
            ...vehicle,
            statut: "en mission",
          });
        }
      }

      let resolvedAmbulanceId = missionData.ambulance_id || null;
      if (!resolvedAmbulanceId && missionData.ambulancier_id) {
        try {
          const localVehicle = vehicles.find(
            (v) => v.user_id === missionData.ambulancier_id
          );
          if (localVehicle) {
            resolvedAmbulanceId = localVehicle.id;
          } else {
            const vehiclesRes2 = await apiClient.get(`${BASE_URL}/vehicules`);
            const found = vehiclesRes2.data.find(
              (v) => v.user_id === missionData.ambulancier_id
            );
            if (found) {
              resolvedAmbulanceId = found.id;
            }
          }
        } catch (e) {
        }
      }

      // Gérer loc_destination si adresse_destination est fournie
      let locDestination = null;
      const destinationLocObj = missionLocations["destination"];
      if (
        destinationLocObj &&
        typeof destinationLocObj.lat === "number" &&
        typeof destinationLocObj.lng === "number"
      ) {
        locDestination = `${destinationLocObj.lat},${destinationLocObj.lng}`;
      }

      // Gérer loc (coordonnées de la mission) - priorité à missionLocations["new"]
      let loc = missionData.loc || null;
      const locObj = missionLocations["new"];
      if (locObj && typeof locObj.lat === "number" && typeof locObj.lng === "number") {
        loc = `${locObj.lat},${locObj.lng}`;
      }

      // Construire la cause pour Acte infirmier si elle n'est pas déjà remplie
      let finalCause = missionData.cause;
      if (missionData.type_mission === "Acte infirmier" && !finalCause) {
        if (missionData.acte_technique) {
          finalCause = `Actes techniques - ${missionData.acte_technique}`;
        } else if (missionData.acte_surveillance) {
          finalCause = `Actes de surveillance médicale - ${missionData.acte_surveillance}`;
        } else if (missionData.acte_prelevement) {
          finalCause = `Actes de prélèvements - ${missionData.acte_prelevement}`;
        } else if (missionData.acte_type) {
          finalCause = missionData.acte_type;
        }
      }

      const finalMissionData = {
        patient_id: patientId,
        etat_mission: missionData.etat_mission || null,
        statut: missionData.statut || "créée",
        date_mission: missionData.date_mission || null,
        urgence: missionData.urgence !== undefined ? missionData.urgence : true,
        type_mission: missionData.type_mission || null,
        cause: finalCause || missionData.cause || null,
        demande_materiel: missionData.demande_materiel || null,
        ambulancier_id: missionData.ambulancier_id ? parseInt(missionData.ambulancier_id) : null,
        ambulance_id: resolvedAmbulanceId ? parseInt(resolvedAmbulanceId) : null,
        type_ambulance: missionData.type_ambulance || null,
        adresse: missionData.adresse || null, // Nom de l'hôpital mission
        adresse_destination: missionData.adresse_destination || null, // Nom de l'hôpital destination
        hospital_id: missionData.hospital_id ? parseInt(missionData.hospital_id) : null,
        hospital_destination_id: missionData.hospital_destination_id ? parseInt(missionData.hospital_destination_id) : null,
        phone: 1, // Booléen toujours à 1 pour cette page
        doctor_id: missionData.doctor_id ? parseInt(missionData.doctor_id) : null,
        nurse_id: missionData.nurse_id ? parseInt(missionData.nurse_id) : null,
        ville: missionData.ville || null,
        ville_dest: missionData.ville_dest || null,
        perimetre: missionData.perimetre || null,
        loc: loc,
        loc_dest: locDestination || null,
        doc_a: missionData.doc_a ? parseInt(missionData.doc_a) : null,
        doc_b: missionData.doc_b ? parseInt(missionData.doc_b) : null,
        service_a: missionData.service_a || null,
        service_b: missionData.service_b || null,
        ta: missionData.ta || null,
        fc: missionData.fc || null,
        spo2: missionData.spo2 || null,
        glasgow: missionData.glasgow || null,
        autre: missionData.autre || null,
        diagnostic: missionData.diagnostic || null,
        motif: missionData.motif || null,
        accord: "oui", // Toujours "oui"
        créé_par: currentUser?.id ?? null,
        timer: missionData.timer !== undefined ? Math.max(0, 15 - (missionData.timer || 15)) : null,
      };

      delete finalMissionData.add_new_patient;
      delete finalMissionData.new_patient;

      const response = await apiClient.post(`${BASE_URL}/missions`, finalMissionData);
      if (response?.data?.id === null) {
        console.warn("[Mission Create Debug] createMission - Réponse sans id, structure complète:", response);
      }

      // Stocker les coordonnées pour la nouvelle mission
      if (missionLocations["new"]) {
        setMissionLocations((prev) => ({
          ...prev,
          [response.data.id]: missionLocations["new"],
          new: null,
        }));
      }

      // Create notifications for each user involved in the mission
      const notifications = [];

      try {
        // Get user details for the message
        const usersRes = await apiClient.get(`${BASE_URL}/users`);

        // Get patient details
        const patient = patients.find((p) => p.id === patientId) ||
          (missionData.new_patient ? {
            nom: missionData.new_patient.nom || 'N/A',
            prenom: missionData.new_patient.prenom || 'N/A',
            téléphone: missionData.new_patient.téléphone || 'N/A'
          } : null);

        // Create notification for ambulancier if exists
        if (missionData.ambulancier_id) {
          const doctor = missionData.doctor_id ? usersRes.data.find((u) => u.id === missionData.doctor_id) : null;
          const nurse = missionData?.nurse_id ? usersRes.data.find((u) => u.id === missionData.nurse_id) : null;

          const message = `Nouvelle mission:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'}
Demande Matériel: ${missionData.demande_materiel || 'N/A'}

Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}

Médecin:
${doctor?.nom || 'N/A'} ${doctor?.prenom || 'N/A'}
Téléphone: ${doctor?.téléphone || 'N/A'}

Infirmier:
${nurse?.nom || 'N/A'} ${nurse?.prenom || 'N/A'}
Téléphone: ${nurse?.téléphone || 'N/A'}`;

          notifications.push({
            user_id: missionData.ambulancier_id,
            mission_id: response.data.id,
            message: message,
            date_notification: missionData.date_mission || new Date().toISOString(),
            type: "mission",
            statut: "nonlue"
          });
        }

        // Create notification for doctor if exists
        if (missionData.doctor_id) {
          const ambulancier = missionData.ambulancier_id ? usersRes.data.find((u) => u.id === missionData.ambulancier_id) : null;
          const nurse = missionData?.nurse_id ? usersRes.data.find((u) => u.id === missionData.nurse_id) : null;

          const message = `Nouvelle mission:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'} 
Demande Matériel: ${missionData.demande_materiel || 'N/A'}

Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}

Ambulancier:
${ambulancier?.nom || 'N/A'} ${ambulancier?.prenom || 'N/A'}
Téléphone: ${ambulancier?.téléphone || 'N/A'}

Infirmier:
${nurse?.nom || 'N/A'} ${nurse?.prenom || 'N/A'}
Téléphone: ${nurse?.téléphone || 'N/A'}`;

          notifications.push({
            user_id: missionData.doctor_id,
            mission_id: response.data.id,
            message: message,
            date_notification: missionData.date_mission || new Date().toISOString(),
            type: "mission",
            statut: "nonlue"
          });
        }

        // Create notification for nurse if exists in the vehicle
        if (missionData?.nurse_id) {
          const nurse = usersRes.data.find((u) => u.id === missionData.nurse_id);
          const ambulancier = missionData.ambulancier_id ? usersRes.data.find((u) => u.id === missionData.ambulancier_id) : null;
          const doctor = missionData.doctor_id ? usersRes.data.find((u) => u.id === missionData.doctor_id) : null;

          if (nurse) {
            const message = `Nouvelle mission:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'}
Demande Matériel: ${missionData.demande_materiel || 'N/A'}
                  
Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}
                  
Ambulancier:
${ambulancier?.nom || 'N/A'} ${ambulancier?.prenom || 'N/A'}
Téléphone: ${ambulancier?.téléphone || 'N/A'}
                  
Médecin:
${doctor?.nom || 'N/A'} ${doctor?.prenom || 'N/A'}
Téléphone: ${doctor?.téléphone || 'N/A'}`;

            notifications.push({
              user_id: missionData.nurse_id,
              mission_id: response.data.id,
              message: message,
              date_notification: missionData.date_mission || new Date().toISOString(),
              type: "mission",
              statut: "nonlue"
            });
          }
        }

        // Create notification for nurse if mission type is "Acte infirmier"
        if (missionData.type_mission === "Acte infirmier" && missionData.nurse_id) {
          const nurse = usersRes.data.find((u) => u.id === missionData.nurse_id);
          const doctor = missionData.doctor_id ? usersRes.data.find((u) => u.id === missionData.doctor_id) : null;
          const ambulancier = missionData.ambulancier_id ? usersRes.data.find((u) => u.id === missionData.ambulancier_id) : null;

          if (nurse) {
            const message = `Nouvelle mission d'acte infirmier:
Type: ${missionData.type_mission || 'N/A'}
cause: ${missionData.cause || 'N/A'}
Date: ${missionData.date_mission || 'N/A'}
Adresse: ${missionData.adresse || 'N/A'}
Demande Matériel: ${missionData.demande_materiel || 'N/A'}

Patient:
${patient?.nom || 'N/A'} ${patient?.prenom || 'N/A'}
Téléphone: ${patient?.téléphone || 'N/A'}

Médecin:
${doctor?.nom || 'N/A'} ${doctor?.prenom || 'N/A'}
Téléphone: ${doctor?.téléphone || 'N/A'}

Ambulancier:
${ambulancier?.nom || 'N/A'} ${ambulancier?.prenom || 'N/A'}
Téléphone: ${ambulancier?.téléphone || 'N/A'}`;

            notifications.push({
              user_id: missionData.nurse_id,
              mission_id: response.data.id,
              message: message,
              date_notification: missionData.date_mission || new Date().toISOString(),
              type: "mission",
              statut: "nonlue"
            });
          }
        }

        // Create all notifications
        for (const notification of notifications) {
          try {
            await apiClient.post(`${BASE_URL}/notifications`, notification);
          } catch (err) {
          }
        }
      } catch (notifErr) {
        // Ne pas bloquer la création de mission si les notifications échouent
      }

      // SMS + map pour tous les types de missions
      const missionId = response.data.id;

      if (missionData.type_mission === "ambulance") {
        const vehicle = vehicles.find((v) => v.user_id === missionData.ambulancier_id);
        const trackerId = vehicle?.tracker_id || null;

        const id = uuidv4();
        const map = {
          id,
          missionid: missionId,
          loc,
          tracker_id: trackerId,
          fin: 0,
        };

        try {
          await apiClient.post(`${BASE_URL}/map`, map);

          const phone =
            patients.find((p) => p.id === patientId)?.téléphone ||
            missionData.new_patient?.téléphone;

          if (phone && map.id) {
            const message = `Omnidoc Santé\nVotre ambulance est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
            try {
              await sendSMS(phone, message);
            } catch (smsErr) {
              // Ne pas bloquer la création de mission si l'SMS échoue
            }
          }
        } catch (err) {
          // Ne pas bloquer la création de mission si la map échoue
        }
      }
      // Cas Consultation à domicile -> map avec userid = doctor_id
      else if (missionData.type_mission === "Consultation à domicile" && missionData.doctor_id) {
        const id = uuidv4();
        const map = {
          id: id,
          missionid: missionId,
          loc: loc,
          userid: parseInt(missionData.doctor_id),
          fin: 0
        };

        try {
          await apiClient.post(`${BASE_URL}/map`, map);

          // SMS patient avec lien de suivi
          const phone =
            patients.find((p) => p.id === patientId)?.téléphone ||
            missionData.new_patient?.téléphone;

          if (phone && map.id) {
            const message = `Omnidoc Santé\nVotre médecin est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
            try {
              await sendSMS(phone, message);
            } catch (smsErr) {
            }
          }
        } catch (err) {
        }
      }
      // Cas Acte infirmier -> map avec userid = nurse_id
      else if (missionData.type_mission === "Acte infirmier" && missionData.nurse_id) {
        const id = uuidv4();
        const map = {
          id: id,
          missionid: missionId,
          loc: loc,
          userid: parseInt(missionData.nurse_id),
          fin: 0
        };

        try {
          await apiClient.post(`${BASE_URL}/map`, map);

          // SMS patient avec lien de suivi
          const phone =
            patients.find((p) => p.id === patientId)?.téléphone ||
            missionData.new_patient?.téléphone;

          if (phone && map.id) {
            const message = `Omnidoc Santé\nVotre infirmier est en route. Suivez sa localisation en direct ici : ${process.env.REACT_APP_BASE_URL_PROD}/prestation/${map.id}`;
            try {
              await sendSMS(phone, message);
            } catch (smsErr) {
            }
          }
        } catch (err) {
        }
      }

      toast({
        title: "Mission ajoutée avec succès",
        status: "success",
        isClosable: true,
      });

      // Lier l'appel en attente à la mission créée si un appel est en attente
      try {
        const pendingCallStr = localStorage.getItem('pending_call_for_mission');
        if (pendingCallStr) {
          const pendingCall = JSON.parse(pendingCallStr);

          if (pendingCall.uniqueid) {
            // Utiliser le chemin audio depuis pendingCall, ou le récupérer depuis localStorage
            let audioPath = pendingCall.audioPath || null;

            if (!audioPath) {
              try {
                const audioInfoStr = localStorage.getItem(`call_audio_${pendingCall.uniqueid}`);
                if (audioInfoStr) {
                  const audioInfo = JSON.parse(audioInfoStr);
                  audioPath = audioInfo.audioPath;
                }
              } catch (e) {
                console.warn('Impossible de récupérer le chemin audio:', e);
              }
            }

            // Si toujours pas de chemin, construire directement (appel peut être encore en cours)
            if (!audioPath && pendingCall.uniqueid) {
              // Format: /uploads/DD-MM-YYYY/uniqueid.webm
              const today = dayjs();
              const dateStr = today.format('DD-MM-YYYY');
              audioPath = `/uploads/${dateStr}/${pendingCall.uniqueid}.webm`;
            }

            // Lier l'appel à la mission créée
            const linkPayload = {
              missionid: response.data.id,
              callid: pendingCall.uniqueid, // Utiliser l'uniqueid comme callid
              lien: audioPath, // Chemin du fichier audio
            };

            const linkResponse = await apiClient.post('/missioncalls', linkPayload);

            if (linkResponse.data.success || linkResponse.status === 200 || linkResponse.status === 201) {
              // Supprimer l'appel en attente
              localStorage.removeItem('pending_call_for_mission');
              toast({
                title: 'Appel lié',
                description: `L'appel a été lié à la mission #${response.data.id}`,
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }
          }
        }
      } catch (linkErr) {
        // Ne pas bloquer la création de mission si la liaison échoue
        console.error('❌ Erreur liaison appel-mission:', linkErr);
        console.error('❌ Détails erreur:', linkErr.response?.data || linkErr.message);
        toast({
          title: 'Avertissement',
          description: 'La mission a été créée mais la liaison avec l\'appel a échoué. Vous pourrez la lier manuellement.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }

      return response.data;
    } catch (err) {
      // Debug: erreur création mission
      console.error("[Mission Create Debug] createMission - Erreur:", err);
      console.error("[Mission Create Debug] createMission - err.response:", err?.response);
      console.error("[Mission Create Debug] createMission - err.response?.data:", err?.response?.data);
      console.error("[Mission Create Debug] createMission - err.response?.status:", err?.response?.status);
      toast({
        title: "Erreur lors de la création de la mission",
        status: "error",
        isClosable: true,
      });
    }
  };

  const handleCreate = async () => {
    if (isAdding) {
      return;
    }

    if (!validateAllFields()) {
      return;
    }

    try {
      setIsAdding(true);
      let patientId = newMissionData.patient_id;

      // Si aucun patient existant n'est lié, créer un patient complet à partir des infos saisies.
      if (!patientId && newMissionData.new_patient) {
        try {
          const createdPatientRes = await apiClient.post(`${BASE_URL}/patients`, {
            ipp: newMissionData.new_patient.ipp || null,
            nom: newMissionData.new_patient.nom || null,
            prenom: newMissionData.new_patient.prenom || null,
            age: newMissionData.new_patient.age || null,
            sexe: newMissionData.new_patient.sexe || null,
            téléphone: newMissionData.new_patient.téléphone || null,
            cin: newMissionData.new_patient.cin || null,
            adresse: newMissionData.new_patient.adresse || null,
            ville: newMissionData.new_patient.ville || null,
          });
          patientId = createdPatientRes?.data?.id || patientId;
          if (patientId) {
            setNewMissionData((prev) => ({ ...prev, patient_id: patientId }));
          }
        } catch (patientCreateErr) {
          console.warn("Erreur lors de la création du patient:", patientCreateErr);
        }
      }

      // Mettre à jour le patient existant avec les nom/prénom et autres infos
      if (patientId && newMissionData.new_patient) {
        try {
          // Mise à jour du patient : inclure l'IPP pour ne pas le supprimer (certaines API remplacent tout le document)
          const updatePatientData = {
            ipp: newMissionData.new_patient.ipp ?? null,
            nom: newMissionData.new_patient.nom || null,
            prenom: newMissionData.new_patient.prenom || null,
            age: newMissionData.new_patient.age || null,
            sexe: newMissionData.new_patient.sexe || null,
            téléphone: newMissionData.new_patient.téléphone || null,
            cin: newMissionData.new_patient.cin || null,
            adresse: newMissionData.new_patient.adresse || null,
            ville: newMissionData.new_patient.ville || null,
          };
          await apiClient.put(`/patients/${patientId}`, updatePatientData);
          // Mettre à jour la liste des patients
          await fetchPatients();
        } catch (patientErr) {
          // Continuer même si la mise à jour du patient échoue
          console.warn("Erreur lors de la mise à jour du patient:", patientErr);
        }
      }

      // même contrôle de mission planifiée pour ambulancier que dans index.jsx
      if (newMissionData.ambulancier_id) {
        const missionsRes = await apiClient.get(`${BASE_URL}/missions`);

        const existingMission = missionsRes.data.find(
          (mission) =>
            mission.ambulancier_id === newMissionData.ambulancier_id &&
            (mission.etat_mission === "planifiée" || mission.etat_mission === "Aller-Retour")
        );

        if (existingMission) {
          const timeDiff =
            new Date(existingMission.date_mission) -
            new Date(newMissionData.date_mission);
          const hours = Math.floor(timeDiff / (1000 * 60 * 60));

          if (hours >= -12 && hours < 12) {
            toast({
              title: "Attention",
              description:
                "Cet ambulancier a déjà une mission planifiée proche dans le temps.",
              status: "warning",
              isClosable: true,
            });
          }
        }
      }

      // Si la mission a été créée partiellement, on la met à jour
      if (partialMissionId) {
        const response = await updateMission(partialMissionId, newMissionData, patientId);
        if (response) {
          // Récupérer les données complètes de la mission
          const missionRes = await apiClient.get(`/missions/${partialMissionId}`);
          setCreatedMissionData(missionRes.data);
          setCreatedMissionId(partialMissionId);
          setPartialMissionId(null); // Réinitialiser
        }
      } else {
        const response = await createMission(newMissionData, patientId);
        if (response && response.id) {
          // Récupérer les données complètes de la mission
          const missionRes = await apiClient.get(`/missions/${response.id}`);
          setCreatedMissionData(missionRes.data);
          setCreatedMissionId(response.id);
        } else {
        }
      }
    } finally {
      setIsAdding(false);
    }
  };

  // Fonction pour créer la mission partiellement depuis l'étape Affectation
  const handleCreatePartial = async () => {
    if (isAdding) return;

    // Vérifier que l'ambulancier est sélectionné
    if (!newMissionData.ambulancier_id) {
      toast({
        title: "Ambulancier requis",
        description: "Veuillez sélectionner un ambulancier",
        status: "error",
        isClosable: true,
      });
      return;
    }


    try {
      setIsAdding(true);
      const response = await createPartialMission(newMissionData);
      if (response && response.id) {
        setPartialMissionId(response.id);
        // Mémoriser le patient_id renvoyé par l'API pour pouvoir mettre à jour le patient (nom, prénom, âge, sexe, CIN) à l'étape 3
        if (response.patient_id != null) {
          setNewMissionData((prev) => ({ ...prev, patient_id: response.patient_id }));
        }
        setActiveStep(2);
      }
    } catch (err) {
    } finally {
      setIsAdding(false);
    }
  };

  useEffect(() => {
    const checkTokenAndUser = async () => {
      try {
        // Vérifier la session côté backend (le backend lit le cookie httpOnly auth_token)
        const check = await checkToken();
        if (!check.data.valid) {
          navigate(`/auth/sign-in`);
          return;
        }

        // Récupérer l'utilisateur courant (le backend lit le cookie)
        const user = await getUser();
        const userRole = user.rôle || user["rÃ´le"] || user.role;
        if (
          userRole !== "admin" &&
          userRole !== "régulateur" &&
          userRole !== "rÃ©gulateur"
        ) {
          navigate(`/auth/sign-in`);
          return;
        }
      } catch (error) {
        navigate(`/auth/sign-in`);
      }
    };
    checkTokenAndUser();
  }, [navigate]);

  useEffect(() => {
    fetchPatients();
    fetchVehicles();
    fetchDoctors();
    fetchNurses();
    fetchAllAmbulanciers();
    fetchHopitaux();
    fetchHospitalVilles();
  }, [fetchPatients, fetchVehicles, fetchDoctors, fetchNurses, fetchAllAmbulanciers, fetchHopitaux, fetchHospitalVilles]);

  // Déterminer automatiquement le périmètre (Urbain / Interurbain)
  useEffect(() => {
    setNewMissionData((prev) => {
      const ville = (prev.ville || "").trim();
      const villeDest = (prev.ville_dest || "").trim();

      // Si aucune ville mission, ne rien faire
      if (!ville) return prev;

      let newPerimetre = prev.perimetre;

      if (prev.type_mission === "ambulance") {
        if (!villeDest) {
          // Ambulance sans ville destination définie: considérer comme Urbain
          newPerimetre = "Urbain";
        } else {
          // Comparaison ville mission / ville destination
          newPerimetre = villeDest === ville ? "Urbain" : "Interurbain";
        }
      } else {
        // Autres types de missions: une seule ville → Urbain
        newPerimetre = "Urbain";
      }

      if (newPerimetre === prev.perimetre) return prev;
      return {
        ...prev,
        perimetre: newPerimetre,
      };
    });
  }, [newMissionData.type_mission, newMissionData.ville, newMissionData.ville_dest]);

  // Mode de transport masqué : TAR par défaut — charger les ambulanciers quand la ville mission est connue
  useEffect(() => {
    if (newMissionData.type_mission !== "ambulance" || !newMissionData.ville) return;
    const ta = newMissionData.type_ambulance || "TAR";
    fetchAmbulanciers(ta, {
      ville: newMissionData.ville,
      etat_mission: newMissionData.etat_mission,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newMissionData.type_mission,
    newMissionData.ville,
    newMissionData.etat_mission,
    newMissionData.type_ambulance,
  ]);

  // Filtrer les villes selon le type de mission (villes depuis GET /hopitals/villes pour adresse mission)
  const filteredVilles = useMemo(() => {
    const missionType = newMissionData.type_mission?.toLowerCase();
    // Pour l'adresse de mission (hôpitaux) : villes depuis GET /hopitals/villes
    if (missionType !== "téléconsultation") {
      if (hospitalVillesList.length > 0) return hospitalVillesList;
    }
    if (missionType === "consultation à domicile" || missionType === "consultation a domicile") {
      const doctorVilles = doctors
        .map((doctor) => doctor.ville)
        .filter((ville) => ville && ville.trim() !== "");
      return [...new Set(doctorVilles)].sort();
    } else if (missionType === "acte infirmier") {
      const nurseVilles = nurses
        .map((nurse) => nurse.ville)
        .filter((ville) => ville && ville.trim() !== "");
      return [...new Set(nurseVilles)].sort();
    } else {
      return uniqueVilles.sort();
    }
  }, [newMissionData.type_mission, doctors, nurses, uniqueVilles, hospitalVillesList]);

  const availableVehiclesForAffectation = useMemo(() => {
    return vehicles.filter(
      (vehicle) =>
        vehicle.user_id !== null &&
        vehicle.user_id !== undefined &&
        vehicle.user_id !== 0 &&
        String(vehicle.user_id).trim() !== "" &&
        String(vehicle.user_id).trim() !== "0" &&
        String(vehicle.statut || "").toLowerCase() === "disponible"
    );
  }, [vehicles]);

  // Réinitialiser la ville si elle n'est plus valide pour le nouveau type de mission
  useEffect(() => {
    if (newMissionData.ville && filteredVilles.length > 0 && newMissionData.type_mission) {
      const isVilleValid = filteredVilles.includes(newMissionData.ville);
      if (!isVilleValid) {
        setNewMissionData((prev) => ({
          ...prev,
          ville: "",
        }));
      }
    }
  }, [filteredVilles, newMissionData.type_mission, newMissionData.ville]);

  // Mettre à jour automatiquement le champ "cause" quand la gravité change
  useEffect(() => {
    const generatedCause = generateCause(newMissionData);
    if (generatedCause !== newMissionData.cause) {
      setNewMissionData((prev) => ({
        ...prev,
        cause: generatedCause,
      }));
    }
  }, [
    newMissionData,
  ]);


  // Timer de 15 secondes qui compte à rebours (peut aller en négatif)
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    // Nettoyer l'intervalle précédent si il existe
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Pour Acte infirmier, forcer l'état à "planifiée"
    if (newMissionData.type_mission === "Acte infirmier" && newMissionData.etat_mission !== "planifiée") {
      setNewMissionData((prev) => ({
        ...prev,
        etat_mission: "planifiée",
      }));
    }

    // Démarrer le timer à l'étape 1 (Informations de mission) pour toutes les missions
    if (activeStep === 0) {
      // Réinitialiser le timer à 15 quand on arrive à l'étape 1
      setNewMissionData((prev) => ({
        ...prev,
        timer: prev.timer === undefined || prev.timer === null ? 15 : prev.timer,
      }));

      // Démarrer le compte à rebours (peut aller en négatif)
      timerIntervalRef.current = setInterval(() => {
        setNewMissionData((prev) => {
          const currentTimer = prev.timer !== undefined && prev.timer !== null ? prev.timer : 15;
          return {
            ...prev,
            timer: currentTimer - 1, // Continue même en négatif
          };
        });
      }, 1000);
    } else {
      // Arrêter le timer quand on quitte l'étape 2, mais ne pas réinitialiser
      // pour garder la valeur actuelle
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [activeStep, newMissionData.etat_mission, newMissionData.type_mission]); // Ne dépendre que de activeStep

  return (
    <Box p="20px" pt="120px" bg="gray.50">
      {/* Fil d'Ariane */}
      <HStack spacing="10px" mb="20px" align="center">
        <IconButton
          icon={<Icon as={MdArrowBack} />}
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/missions`)}
          aria-label="Retour"
          w="36px"
          h="36px"
          borderRadius="6px"
          _hover={{ bg: "gray.100" }}
        />
        <Text
          fontSize="14px"
          color="#656C7A"
          cursor="pointer"
          _hover={{ color: "#455879" }}
          onClick={() => navigate(`/admin/missions`)}
        >
          Gestion de mission
        </Text>
        <Icon
          as={MdArrowForward}
          w="12px"
          h="12px"
          color="#656C7A"
          transform="rotate(-90deg)"
        />
        <Text
          fontSize="14px"
          color="#656C7A"
          cursor="pointer"
          _hover={{ color: "#455879" }}
          onClick={() => navigate(`/admin/missions`)}
        >
          Missions
        </Text>
        <Icon
          as={MdArrowForward}
          w="12px"
          h="12px"
          color="#656C7A"
          transform="rotate(-90deg)"
        />
        <Text fontSize="14px" color="#455879" fontWeight="600">
          Créer une mission
        </Text>
      </HStack>

      {!loaded ? (
        <Box textAlign="center" py={4}>
          <LoadingSpinner message="Chargement de l'API Google Maps..." size="md" />
        </Box>
      ) : createdMissionId ? (
        // Écran de confirmation selon le design
        <Box
          bg="white"
          borderRadius="6px"
          p="40px"
          border="1px solid"
          borderColor="gray.200"
          textAlign="center"
          maxW="600px"
          mx="auto"
        >
          {/* Icône avec checkmark bleu */}
          <Box
            w="80px"
            h="80px"
            mb="30px"
            mx="auto"
            bg="#E3F2FD"
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="0px 4px 12px rgba(69,156,233,0.2)"
          >
            <Icon as={MdCheckCircle} w="50px" h="50px" color="#006133" />
          </Box>

          <VStack spacing="20px" align="stretch" w="100%">
            {/* Titre */}
            <Text
              fontSize="20px"
              fontWeight="600"
              color="#455879"
              textAlign="center"
            >
              Mission créée avec succès.
            </Text>

            {/* Réf mission et Statut */}
            <Box>
              <Text fontSize="14px" color="#455879" lineHeight="1.6">
                Réf mission{" "}
                <Text as="span" fontWeight="600">
                  {`MTC-${String(createdMissionId).padStart(4, '0')}`}
                </Text>{" "}
                Statut •{" "}
                <Text as="span" fontWeight="600">
                  {(() => {
                    const statut = createdMissionData?.statut || createdMissionData?.etat_mission || "En cours";
                    return statut.charAt(0).toUpperCase() + statut.slice(1).toLowerCase();
                  })()}
                </Text>{" "}
                <Text
                  as="span"
                  color="#006133"
                  cursor="pointer"
                  textDecoration="underline"
                  onClick={() => navigate(`/admin/missions/${createdMissionId}`)}
                  _hover={{ color: "#3a8bd4" }}
                >
                  Voir la mission
                </Text>
              </Text>
            </Box>

            {/* Texte explicatif */}
            <Text fontSize="14px" color="#656C7A" lineHeight="1.6" textAlign="center">
              La mission a été enregistrée avec succès dans le système. Vous pouvez désormais consulter les détails complets de cette mission, revenir à la liste générale, ou créer une nouvelle mission si nécessaire.
            </Text>

            {/* Boutons */}
            <HStack justify="center" spacing="15px" mt="10px">
              <Button
                bg="#006133"
                color="white"
                _hover={{ bg: "#3a8bd4" }}
                leftIcon={<Icon as={MdAdd} />}
                onClick={() => {
                  setCreatedMissionId(null);
                  setCreatedMissionData(null);
                  setNewMissionData({
                    type_mission: "",
                    cause: "",
                    date_mission: dayjs()
                      .tz("Africa/Casablanca")
                      .format("YYYY-MM-DDTHH:mm"),
                    adresse: "",
                    adresse_destination: "",
                    demande_materiel: "",
                    patient_id: "",
                    doctor_id: "",
                    ambulance_id: "",
                    statut: "créée",
                    ville: "",
                    type_ambulance: "TAR",
                    ambulancier_id: "",
                    nurse_id: "",
                    etat_mission: "urgente",
                    urgence: true,
                    gravite: "",
                    urgence_vitale: "",
                    urgence_vitale_type: "",
                    urgence_elevee: "",
                    urgence_elevee_type: "",
                    non_vitale: [],
                    ta: "",
                    fc: "",
                    spo2: "",
                    glasgow: "",
                    autre: "",
                    diagnostic: "",
                    motif: "",
                    add_new_patient: false,
                    perimetre: "",
                    loc: "",
                    hospital_id: "",
                    hospital_destination_id: "",
                    ville_dest: "",
                    doc_a: "",
                    doc_b: "",
                    service_a: "",
                    service_b: "",
                    accord_destination: "oui",
                    timer: 15, // Réinitialiser le timer
                    new_patient: {
                      nom: "",
                      prenom: "",
                      age: "",
                      sexe: "",
                      téléphone: "",
                      cin: "",
                      ipp: "",
                      adresse: "",
                      ville: "",
                    },
                  });
                  setActiveStep(0);
                }}
              >
                + Créer une nouvelle mission
              </Button>
              <Button
                variant="outline"
                borderColor="gray.300"
                color="#455879"
                _hover={{ bg: "gray.50" }}
                onClick={() => navigate(`/admin/missions`)}
              >
                Retour à la liste
              </Button>
            </HStack>
          </VStack>
        </Box>
      ) : (
        <Flex
          bg="white"
          borderRadius="6px"
          p="24px"
          border="1px solid"
          borderColor="gray.200"
          gap="32px"
          align="flex-start"
        >
          {/* Sidebar Stepper */}
          <Box
            bg="blue.50"
            borderRadius="6px"
            p="20px"
            minW="260px"
            alignSelf="flex-start"
          >
            <Stepper index={activeStep} orientation="vertical" gap="0" colorScheme="blue">
              {steps.map((step, index) => {
                const isComplete = index < activeStep;
                const isActive = index === activeStep;
                return (
                  <Step key={index}>
                    <StepIndicator
                      borderColor={
                        isComplete
                          ? "#29BACD"
                          : isActive
                            ? "#006133"
                            : "rgba(69,156,233,0.2)"
                      }
                      borderWidth="1px"
                      borderRadius="100px"
                      size="28px"
                    >
                      <StepStatus
                        complete={
                          <Box
                            w="28px"
                            h="28px"
                            borderRadius="100px"
                            bg="#29BACD"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Icon as={MdCheckCircle} w="16px" h="16px" color="white" />
                          </Box>
                        }
                        incomplete={
                          <Box
                            w="28px"
                            h="28px"
                            borderRadius="100px"
                            border="1px solid"
                            borderColor="rgba(69,156,233,0.2)"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <StepNumber />
                          </Box>
                        }
                        active={
                          <Box
                            w="28px"
                            h="28px"
                            borderRadius="100px"
                            border="1px solid"
                            borderColor="#006133"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <StepNumber color="#006133" />
                          </Box>
                        }
                      />
                    </StepIndicator>
                    <Box flexShrink="0" w="220px">
                      <StepTitle
                        fontSize={isActive ? "14px" : "13px"}
                        fontWeight={isActive || isComplete ? "500" : "400"}
                        color={
                          isComplete ? "#29BACD" : isActive ? "#006133" : "#455879"
                        }
                      >
                        {step.title}
                      </StepTitle>
                      <StepDescription fontSize="11px" color="#455879">
                        {step.description}
                      </StepDescription>
                    </Box>
                    {index < steps.length - 1 && <StepSeparator />}
                  </Step>
                );
              })}
            </Stepper>
          </Box>

          {/* Contenu principal (formulaire complet) */}
          <Box flex="1">
            {/* Header avec titre et timer - visible dans toutes les étapes */}
            <Flex justify="space-between" mb="20px" align="center" gap="10px">
              <VStack align="flex-start" spacing="6px" flex="1">
                <Text fontSize="18px" fontWeight="600" color="#455879">
                  {(steps && steps[activeStep]?.title) || "Créer une nouvelle mission"}
                </Text>
                <Text fontSize="13px" color="#656C7A">
                  {(steps && steps[activeStep]?.description) || "Renseignez les informations de la mission, de l'intervenant et du patient."}
                </Text>
              </VStack>

              {/* Timer - visible dans toutes les étapes */}
              <HStack spacing="10px" flexShrink={0}>
                {/* Timer */}
                <Box
                  bg="white"
                  border="1px solid"
                  borderColor="#E5E5E5"
                  borderRadius="6px"
                  p="10px"
                  display="flex"
                  alignItems="center"
                  gap="5px"
                >
                  <Icon
                    as={MdTimer}
                    w="16px"
                    h="16px"
                    color={(newMissionData.timer || 15) <= 0 ? "#F8285A" : "#006133"}
                  />
                  <Text
                    fontSize="12px"
                    fontWeight="700"
                    color={(newMissionData.timer || 15) <= 0 ? "#F8285A" : "#455879"}
                    fontFamily="Poppins, sans-serif"
                    sx={{
                      "@keyframes blink": {
                        "0%, 100%": { opacity: 1 },
                        "50%": { opacity: 0.3 },
                      },
                      animation: (newMissionData.timer || 15) <= 0 ? "blink 1s infinite" : "none",
                    }}
                  >
                    {(() => {
                      const timerValue = newMissionData.timer !== undefined && newMissionData.timer !== null ? newMissionData.timer : 15;
                      const absValue = Math.abs(timerValue);
                      const sign = timerValue < 0 ? "-" : "";
                      return `${sign}${String(Math.floor(absValue / 60)).padStart(2, "0")}:${String(absValue % 60).padStart(2, "0")}`;
                    })()}
                  </Text>
                </Box>
              </HStack>
            </Flex>

            <VStack spacing="24px" align="stretch">
              {/* Étape 1 : Informations de mission */}
              {activeStep === 0 && (
                <VStack spacing="14px" align="stretch">
                  {/* Section 1: Informations destinées aux État de mission */}
                  <Box
                    bg="#FCFCFC"
                    border="1px solid"
                    borderColor="#F9F9F9"
                    borderRadius="6px"
                    p="14px"
                  >
                    <Text
                      fontSize="11px"
                      fontWeight="400"
                      color="#656C7A"
                      mb="20px"
                      lineHeight="1.2"
                    >
                      Informations destinées aux État de mission
                    </Text>
                    <VStack spacing="20px" align="stretch">
                      {/* État de mission et Gravité côte à côte */}
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                        {/* État de mission */}
                        <FormControl isRequired isInvalid={!!invalidFields.etat_mission}>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            État de mission
                          </FormLabel>
                          <RadioGroup
                            value={newMissionData.etat_mission === "Aller-Retour" ? "planifiée" : newMissionData.etat_mission}
                            onChange={(value) => {
                              setNewMissionData((prev) => ({
                                ...prev,
                                etat_mission: value === "planifiée" ? "planifiée" : value,
                                urgence: value === "urgente",
                                date_mission:
                                  value === "urgente"
                                    ? dayjs()
                                      .tz("Africa/Casablanca")
                                      .format("YYYY-MM-DDTHH:mm")
                                    : prev.date_mission,
                              }));
                            }}
                          >
                            <HStack spacing="20px" py="12px">
                              <Radio value="urgente" colorScheme="blue">
                                Urgente
                              </Radio>
                              <Radio value="planifiée" colorScheme="blue">
                                Planifiée
                              </Radio>
                            </HStack>
                          </RadioGroup>
                        </FormControl>

                        {/* Gravité - cachée pour missions ambulance planifiées */}
                        {!(newMissionData.type_mission === "ambulance" &&
                          (newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour")) && (
                            <FormControl isRequired isInvalid={!!invalidFields.gravite}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Gravité
                              </FormLabel>
                              <RadioGroup
                                value={newMissionData.gravite}
                                onChange={(value) => {
                                  setNewMissionData((prev) => ({
                                    ...prev,
                                    gravite: value,
                                    urgence_vitale: value === "Urgence vitale" ? prev.urgence_vitale : "",
                                    urgence_vitale_type: value === "Urgence vitale" ? prev.urgence_vitale_type : "",
                                    urgence_elevee: value === "Urgence élevée" ? prev.urgence_elevee : "",
                                    urgence_elevee_type: value === "Urgence élevée" ? prev.urgence_elevee_type : "",
                                    non_vitale: value === "Non vitale" ? prev.non_vitale : [],
                                  }));
                                }}
                              >
                                <HStack spacing="20px" py="12px">
                                  <Radio value="Urgence vitale" colorScheme="blue">
                                    Urgence vitale
                                  </Radio>
                                  <Radio value="Urgence élevée" colorScheme="blue">
                                    Urgence élevée
                                  </Radio>
                                  <Radio value="Non vitale" colorScheme="blue">
                                    Non vitale
                                  </Radio>
                                </HStack>
                              </RadioGroup>
                            </FormControl>
                          )}

                        {/* Type de trajet - affiché uniquement pour missions ambulance planifiées */}
                        {newMissionData.type_mission === "ambulance" &&
                          (newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour") && (
                            <FormControl isRequired>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Type de trajet
                              </FormLabel>
                              <RadioGroup
                                value={newMissionData.etat_mission === "Aller-Retour" ? "Aller-Retour" : "Aller simple"}
                                onChange={(value) => {
                                  setNewMissionData((prev) => ({
                                    ...prev,
                                    etat_mission: value === "Aller simple" ? "planifiée" : "Aller-Retour",
                                  }));
                                }}
                              >
                                <HStack spacing="20px" py="12px">
                                  <Radio value="Aller simple" colorScheme="blue">
                                    Aller simple
                                  </Radio>
                                  <Radio value="Aller-Retour" colorScheme="blue">
                                    Aller-Retour
                                  </Radio>
                                </HStack>
                              </RadioGroup>
                            </FormControl>
                          )}
                      </SimpleGrid>

                      {/* eslint-disable-next-line no-mixed-operators */}
                      {(newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour") && (
                        <FormControl isRequired isInvalid={!!invalidFields.date_mission}>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879">
                            Date de Mission
                          </FormLabel>
                          <Input
                            type="datetime-local"
                            value={newMissionData.date_mission}
                            onChange={(e) =>
                              setNewMissionData((prev) => ({
                                ...prev,
                                date_mission: e.target.value,
                              }))
                            }
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                          />
                        </FormControl>
                      )}

                      {/* Urgence vitale (si gravité = Urgence vitale) - cachée pour missions ambulance planifiées */}
                      {newMissionData.gravite === "Urgence vitale" &&
                        !(newMissionData.type_mission === "ambulance" &&
                          (newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour")) && (
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                            <FormControl isRequired isInvalid={!!invalidFields.urgence_vitale}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Urgence vitale
                              </FormLabel>
                              <RadioGroup
                                value={newMissionData.urgence_vitale}
                                onChange={(value) => {
                                  setNewMissionData((prev) => ({
                                    ...prev,
                                    urgence_vitale: value,
                                    urgence_vitale_type: "",
                                  }));
                                }}
                              >
                                <HStack spacing="20px" py="12px">
                                  <Radio value="Cardiaque" colorScheme="blue">
                                    Cardiaque
                                  </Radio>
                                  <Radio value="Neurologique" colorScheme="blue">
                                    Neurologique
                                  </Radio>
                                  <Radio value="Respiratoire" colorScheme="blue">
                                    Respiratoire
                                  </Radio>
                                </HStack>
                              </RadioGroup>
                            </FormControl>

                            {/* Type d'urgence vitale (Cardiaque, Neurologique ou Respiratoire) */}
                            {newMissionData.urgence_vitale && (
                              <FormControl >
                                <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                  {newMissionData.urgence_vitale}
                                </FormLabel>
                                <Input
                                  value={newMissionData.urgence_vitale_type || ""}
                                  onChange={(e) =>
                                    setNewMissionData((prev) => ({
                                      ...prev,
                                      urgence_vitale_type: e.target.value,
                                    }))
                                  }
                                  placeholder={`Saisissez le type (${newMissionData.urgence_vitale})…`}
                                  h="42px"
                                  borderRadius="6px"
                                  border="1px solid"
                                  borderColor={invalidFields.urgence_vitale_type ? "#E53E3E" : "#E5E5E5"}
                                  boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                  fontSize="13px"
                                  bg="white"
                                />
                              </FormControl>
                            )}
                          </SimpleGrid>
                        )}

                      {/* Urgence élevée (si gravité = Urgence élevée) - cachée pour missions ambulance planifiées */}
                      {newMissionData.gravite === "Urgence élevée" &&
                        !(newMissionData.type_mission === "ambulance" &&
                          (newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour")) && (
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                            <FormControl isRequired isInvalid={!!invalidFields.urgence_elevee}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Urgence élevée
                              </FormLabel>
                              <Input
                                value={newMissionData.urgence_elevee || ""}
                                onChange={(e) =>
                                  setNewMissionData((prev) => ({
                                    ...prev,
                                    urgence_elevee: e.target.value,
                                    // si on change la catégorie à la main, on ne force pas le reset
                                  }))
                                }
                                placeholder="Saisissez la catégorie d'urgence élevée…"
                                h="42px"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor={invalidFields.urgence_elevee ? "#E53E3E" : "#E5E5E5"}
                                boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                fontSize="13px"
                                bg="white"
                              />
                            </FormControl>

                          </SimpleGrid>
                        )}

                      {/* Non vitale (si gravité = Non vitale) - cachée pour missions ambulance planifiées */}
                      {newMissionData.gravite === "Non vitale" &&
                        !(newMissionData.type_mission === "ambulance" &&
                          (newMissionData.etat_mission === "planifiée" || newMissionData.etat_mission === "Aller-Retour")) && (
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                            <FormControl isRequired isInvalid={!!invalidFields.non_vitale}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Non vitale
                              </FormLabel>
                              <Input
                                value={(newMissionData.non_vitale || []).join(", ")}
                                onChange={(e) => {
                                  const raw = e.target.value || "";
                                  const arr = raw
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean);
                                  setNewMissionData((prev) => ({
                                    ...prev,
                                    non_vitale: arr,
                                  }));
                                }}
                                placeholder="Saisissez les éléments séparés par virgule…"
                                h="42px"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor={invalidFields.non_vitale ? "#E53E3E" : "#E5E5E5"}
                                boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                fontSize="13px"
                                bg="white"
                              />
                            </FormControl>
                          </SimpleGrid>
                        )}
                    </VStack>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                        <FormControl>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            TA
                          </FormLabel>
                          <Input
                            value={newMissionData.ta || ""}
                            onChange={(e) => setNewMissionData((prev) => ({ ...prev, ta: e.target.value }))}
                            placeholder="ex: 12/8"
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            bg="white"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            FC
                          </FormLabel>
                          <Input
                            value={newMissionData.fc || ""}
                            onChange={(e) => setNewMissionData((prev) => ({ ...prev, fc: e.target.value }))}
                            placeholder="ex: 82"
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            bg="white"
                          />
                        </FormControl>
                  
                      </SimpleGrid>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                        <FormControl>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            Glasgow
                          </FormLabel>
                          <Input
                            value={newMissionData.glasgow || ""}
                            onChange={(e) => setNewMissionData((prev) => ({ ...prev, glasgow: e.target.value }))}
                            placeholder="ex: 15"
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            bg="white"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            SPO2
                          </FormLabel>
                          <Input
                            value={newMissionData.spo2 || ""}
                            onChange={(e) => setNewMissionData((prev) => ({ ...prev, spo2: e.target.value }))}
                            placeholder="ex: 98%"
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            bg="white"
                          />
                        </FormControl>
                      </SimpleGrid>
                      <SimpleGrid columns={{ base: 1, md: 1 }} spacing="20px">
                      
                        <FormControl>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            Autre
                          </FormLabel>
                          <Input
                            value={newMissionData.autre || ""}
                            onChange={(e) => setNewMissionData((prev) => ({ ...prev, autre: e.target.value }))}
                            placeholder="Autres constantes / remarques"
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            bg="white"
                          />
                        </FormControl>
                      </SimpleGrid>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                        <FormControl>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            Diagnostic principal
                          </FormLabel>
                          <Input
                            value={newMissionData.diagnostic || ""}
                            onChange={(e) => setNewMissionData((prev) => ({ ...prev, diagnostic: e.target.value }))}
                            placeholder="Saisissez le diagnostic principal"
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            bg="white"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            Motif du transport
                          </FormLabel>
                          <Input
                            value={newMissionData.motif || ""}
                            onChange={(e) => setNewMissionData((prev) => ({ ...prev, motif: e.target.value }))}
                            placeholder="Saisissez le motif du transport"
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            bg="white"
                          />
                        </FormControl>
                      </SimpleGrid>

                  </Box>
                  
                  
                  {/* Section 2: Informations destinées aux patients (IPP = première donnée) */}
                  <Box
                    bg="#FCFCFC"
                    border="1px solid"
                    borderColor="#F9F9F9"
                    borderRadius="6px"
                    p="14px"
                  >
                    <Text
                      fontSize="11px"
                      fontWeight="400"
                      color="#656C7A"
                      mb="20px"
                      lineHeight="1.2"
                    >
                      Informations du patient
                    </Text>
                    <VStack spacing="20px" align="stretch">
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                        <FormControl isRequired isInvalid={!!invalidFields.new_patient_ipp}>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            IPP
                          </FormLabel>
                          <Input
                            placeholder="Saisissez l'IPP du patient..."
                            value={newMissionData.new_patient.ipp || ""}
                            onChange={(e) => handlePatientFieldChange("ipp", e.target.value)}
                            h="42px"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            color="#656C7A"
                          />
                        </FormControl>
                        <FormControl isInvalid={!!invalidFields.new_patient_téléphone}>
                          <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                            Téléphone
                          </FormLabel>
                          <InputGroup h="42px">
                            <InputLeftElement
                              w="auto"
                              pl="14px"
                              pr="10px"
                              borderRight="1px solid"
                              borderColor={invalidFields.new_patient_téléphone ? "#E53E3E" : "#E5E5E5"}
                            >
                              <HStack spacing="5px">
                                <Image
                                  src={telephonemarImg}
                                  w="18px"
                                  h="18px"
                                  alt="MA"
                                />
                                <Text fontSize="13px" color="#656C7A">
                                  +212
                                </Text>
                                <Icon
                                  as={MdArrowForward}
                                  w="16px"
                                  h="16px"
                                  transform="rotate(90deg)"
                                  color="#656C7A"
                                />
                              </HStack>
                            </InputLeftElement>
                            <Input
                              pl="120px"
                              placeholder="Saisissez numéro de téléphone..."
                              value={newMissionData.new_patient.téléphone}
                              onChange={(e) => {
                                handlePatientFieldChange("téléphone", e.target.value);
                                if (invalidFields.new_patient_téléphone) {
                                  setInvalidFields(prev => ({ ...prev, new_patient_téléphone: null }));
                                }
                              }}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={10}
                              h="42px"
                              borderRadius="6px"
                              border="1px solid"
                              borderColor={invalidFields.new_patient_téléphone ? "#E53E3E" : "#E5E5E5"}
                              boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            />
                          </InputGroup>
                          {invalidFields.new_patient_téléphone && (
                            <FormErrorMessage fontSize="11px">
                              {invalidFields.new_patient_téléphone}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </SimpleGrid>
                      {shouldShowFullPatientInStep1 && (
                        <>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                            <FormControl isRequired isInvalid={!!invalidFields.new_patient_nom}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Nom
                              </FormLabel>
                              <Input
                                placeholder="Saisissez Nom"
                                value={newMissionData.new_patient.nom || ""}
                                onChange={(e) => handlePatientFieldChange("nom", e.target.value)}
                                h="42px"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor="#E5E5E5"
                                boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                fontSize="13px"
                                color="#656C7A"
                              />
                            </FormControl>
                            <FormControl isRequired isInvalid={!!invalidFields.new_patient_prenom}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Prénom
                              </FormLabel>
                              <Input
                                placeholder="Saisissez Prénom"
                                value={newMissionData.new_patient.prenom || ""}
                                onChange={(e) => handlePatientFieldChange("prenom", e.target.value)}
                                h="42px"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor="#E5E5E5"
                                boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                fontSize="13px"
                                color="#656C7A"
                              />
                            </FormControl>
                          </SimpleGrid>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                            <FormControl isRequired isInvalid={!!invalidFields.new_patient_age}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Âge
                              </FormLabel>
                              <Input
                                type="number"
                                placeholder="Saisissez Âge"
                                value={newMissionData.new_patient.age || ""}
                                onChange={(e) => handlePatientFieldChange("age", e.target.value)}
                                h="42px"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor="#E5E5E5"
                                boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                fontSize="13px"
                                color="#656C7A"
                              />
                            </FormControl>
                            <FormControl isRequired isInvalid={!!invalidFields.new_patient_sexe}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Sexe
                              </FormLabel>
                              <RadioGroup
                                value={normalizeSexeForDisplay(newMissionData.new_patient.sexe)}
                                onChange={(value) => handlePatientFieldChange("sexe", value)}
                              >
                                <HStack spacing="20px" py="12px">
                                  <Radio value="Homme" colorScheme="blue">
                                    Homme
                                  </Radio>
                                  <Radio value="Femme" colorScheme="blue">
                                    Femme
                                  </Radio>
                                </HStack>
                              </RadioGroup>
                            </FormControl>
                          </SimpleGrid>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                            <FormControl isInvalid={!!invalidFields.new_patient_cin}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                CIN (facultatif)
                              </FormLabel>
                              <Input
                                placeholder="Saisissez le CIN du patient"
                                value={newMissionData.new_patient.cin || ""}
                                onChange={(e) => handlePatientFieldChange("cin", e.target.value)}
                                h="42px"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor="#E5E5E5"
                                boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                fontSize="13px"
                                color="#656C7A"
                              />
                            </FormControl>
                            <Box display={{ base: "none", md: "block" }} />
                          </SimpleGrid>
                        </>
                      )}
                    </VStack>
                  </Box>

                  {/* Section 3: Informations destinées aux Adresse de mission (hôpitaux) */}
                  {newMissionData.type_mission !== "Téléconsultation" && (
                    <Box
                      bg="#FCFCFC"
                      border="1px solid"
                      borderColor="#F9F9F9"
                      borderRadius="6px"
                      p="14px"
                    >
                      <Text
                        fontSize="11px"
                        fontWeight="400"
                        color="#656C7A"
                        mb="20px"
                        lineHeight="1.2"
                      >
                        Informations mission
                      </Text>
                      <VStack spacing="20px" align="stretch">
                        {/* Ville mission (adresse mission) */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                          <FormControl isRequired isInvalid={!!invalidFields.ville}>
                            <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                              Ville mission
                            </FormLabel>
                            <VilleDropdown
                              value={newMissionData.ville}
                              isInvalid={!!invalidFields.ville}
                              onChange={(value) => {
                                setNewMissionData((prev) => ({
                                  ...prev,
                                  ville: value,
                                  ambulance_id: "",
                                  hospital_id: "", // Réinitialiser l'hôpital si on change de ville mission
                                }));
                              }}
                              options={filteredVilles}
                            />
                          </FormControl>
                          {/* Colonne vide pour garder la largeur à 50% sur desktop */}
                          <Box display={{ base: "none", md: "block" }} />
                        </SimpleGrid>

                        {/* Hôpital (adresse mission) et Service */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                          <FormControl isRequired isInvalid={!!invalidFields.hospital_id}>
                            <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                              Hôpital / Adresse mission
                            </FormLabel>
                            <HospitalDropdown
                              value={newMissionData.hospital_id}
                              isInvalid={!!invalidFields.hospital_id}
                              onChange={handleHospitalSelect}
                              options={hospitals}
                              placeholder="Sélectionner un hôpital"
                              filterByVille={newMissionData.ville}
                            />
                          </FormControl>

                          {/* Service de l'hôpital mission */}
                          {newMissionData.hospital_id && (
                            <FormControl isRequired isInvalid={!!invalidFields.service_a}>
                              <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                Service
                              </FormLabel>
                              <Input
                                value={newMissionData.service_a || ""}
                                onChange={(e) =>
                                  setNewMissionData((prev) => ({
                                    ...prev,
                                    service_a: e.target.value,
                                  }))
                                }
                                placeholder="Saisissez le service…"
                                h="42px"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor={invalidFields.service_a ? "#E53E3E" : "#E5E5E5"}
                                boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                fontSize="13px"
                                bg="white"
                              />
                            </FormControl>
                          )}
                        </SimpleGrid>

                        {/* Médecin demandeur (doc_a) */}
                        {newMissionData.hospital_id && (
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                            <FormControl isRequired isInvalid={!!invalidFields.doc_a}>
                              <Flex justify="space-between" align="center" mb="10px" gap="10px">
                                <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="0">
                                  Médecin demandeur
                                </FormLabel>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() => openAddDoctorModal("mission")}
                                  isDisabled={!newMissionData.hospital_id}
                                >
                                  Ajouter un médecin
                                </Button>
                              </Flex>
                              <DoctorDropdown
                                value={newMissionData.doc_a}
                                isInvalid={!!invalidFields.doc_a}
                                onChange={(doctorId) =>
                                  setNewMissionData((prev) => ({ ...prev, doc_a: doctorId }))
                                }
                                options={medecinsHopitalMission}
                                isRequired={true}
                              />
                            </FormControl>
                            {/* Colonne vide pour garder la largeur à 50% sur desktop */}
                            <Box display={{ base: "none", md: "block" }} />
                          </SimpleGrid>
                        )}

                        {/* Ville destination + Hôpital destination + Accord avec (pour ambulance) */}
                        {newMissionData.type_mission === "ambulance" && (
                          <>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                              <FormControl isRequired isInvalid={!!invalidFields.ville_dest}>
                                <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                  Ville destination
                                </FormLabel>
                                <VilleDropdown
                                  value={newMissionData.ville_dest}
                                  isInvalid={!!invalidFields.ville_dest}
                                  onChange={(value) => {
                                    setNewMissionData((prev) => ({
                                      ...prev,
                                      ville_dest: value,
                                      hospital_destination_id: "", // Réinitialiser hôpital destination
                                      doc_b: "",
                                    }));
                                  }}
                                  options={hospitalVillesList}
                                />
                              </FormControl>
                              {/* Colonne vide pour garder la largeur à 50% sur desktop */}
                              <Box display={{ base: "none", md: "block" }} />
                            </SimpleGrid>
                            {/* Hôpital destination et Service destination */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                              <FormControl isRequired isInvalid={!!invalidFields.hospital_destination_id}>
                                <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                  Hôpital destination
                                </FormLabel>
                                <HospitalDropdown
                                  value={newMissionData.hospital_destination_id}
                                  isInvalid={!!invalidFields.hospital_destination_id}
                                  onChange={handleHospitalDestinationSelect}
                                  options={hospitals}
                                  placeholder="Sélectionner un hôpital"
                                  filterByVille={newMissionData.ville_dest}
                                />
                              </FormControl>

                              {/* Service de l'hôpital destination */}
                              {newMissionData.hospital_destination_id && (
                                <FormControl isRequired isInvalid={!!invalidFields.service_b}>
                                  <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="10px">
                                    Service destination
                                  </FormLabel>
                                  <Input
                                    value={newMissionData.service_b || ""}
                                    onChange={(e) =>
                                      setNewMissionData((prev) => ({
                                        ...prev,
                                        service_b: e.target.value,
                                      }))
                                    }
                                    placeholder="Saisissez le service destination…"
                                    h="42px"
                                    borderRadius="6px"
                                    border="1px solid"
                                    borderColor={invalidFields.service_b ? "#E53E3E" : "#E5E5E5"}
                                    boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                                    fontSize="13px"
                                    bg="white"
                                  />
                                </FormControl>
                              )}
                            </SimpleGrid>

                            {/* Médecin destinataire (doc_b) - toujours affiché */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                              <FormControl isRequired isInvalid={!!invalidFields.doc_b}>
                                <Flex justify="space-between" align="center" mb="10px" gap="10px">
                                  <FormLabel fontSize="13px" fontWeight="500" color="#455879" mb="0">
                                    Médecin receveur 
                                  </FormLabel>
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={() => openAddDoctorModal("destination")}
                                    isDisabled={!newMissionData.hospital_destination_id}
                                  >
                                    Ajouter un médecin
                                  </Button>
                                </Flex>
                                <DoctorDropdown
                                  value={newMissionData.doc_b}
                                  isInvalid={!!invalidFields.doc_b}
                                  onChange={(doctorId) =>
                                    setNewMissionData((prev) => ({ ...prev, doc_b: doctorId }))
                                  }
                                  options={medecinsHopitalDestination}
                                  isRequired={true}
                                />
                              </FormControl>
                              {/* Colonne vide pour garder la largeur à 50% sur desktop */}
                              <Box display={{ base: "none", md: "block" }} />
                            </SimpleGrid>
                          </>
                        )}
                      </VStack>
                    </Box>
                  )}

                  {/* Mode de transport : masqué, TAR appliqué par défaut (voir newMissionData.type_ambulance) */}
                </VStack>
              )}

              {/* Étape 2 : Affectation */}
              {activeStep === 1 && (
                <Box>
                  <Flex justify="flex-end" mb="10px">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleRefreshEquipe}
                      isLoading={isRefreshingEquipe}
                      loadingText="Rafraîchissement..."
                    >
                      Recharger
                    </Button>
                  </Flex>
                  {newMissionData.type_mission === "ambulance" && (newMissionData.loc || missionLocations.new?.lat) ? (
                    <AffectationMap
                      vehicles={availableVehiclesForAffectation}
                      missionLocation={(() => {
                        // Utiliser en priorité le champ loc de la mission (format "lat,lng"), sinon le missionLocations.new
                        if (newMissionData.loc) {
                          const parsed = parseLocString(newMissionData.loc);
                          if (parsed) return parsed;
                        }
                        return missionLocations.new;
                      })()}
                      selectedAmbulanceId={newMissionData.ambulance_id}
                      onAmbulanceSelect={(ambulanceId) => {
                        // Trouver le véhicule sélectionné pour récupérer l'ambulancier et l'infirmier lié
                        const selectedVehicle = vehicles.find(v => v.id === ambulanceId);
                        setNewMissionData((prev) => ({
                          ...prev,
                          ambulance_id: ambulanceId,
                          ambulancier_id: selectedVehicle?.user_id || "",
                          // Pré-sélectionner l'infirmier lié à cette ambulance si l'info existe sur le véhicule (champ vehicules.infirmier)
                          nurse_id: selectedVehicle?.infirmier || prev.nurse_id || "",
                        }));
                      }}
                      doctors={doctors}
                      ambulanciers={allAmbulanciers}
                      fetchVehicleLocation={fetchVehicleLocation}
                      nurses={nurses}
                      selectedDoctorId={newMissionData.doctor_id}
                      selectedNurseId={newMissionData.nurse_id}
                      onDoctorSelect={(doctorId) => {
                        setNewMissionData((prev) => ({
                          ...prev,
                          doctor_id: doctorId,
                        }));
                      }}
                      onNurseSelect={(nurseId) => {
                        setNewMissionData((prev) => ({
                          ...prev,
                          nurse_id: nurseId,
                        }));
                      }}
                      typeMission={newMissionData.type_mission}
                      typeAmbulance={newMissionData.type_ambulance}
                    />
                  ) : newMissionData.type_mission === "Consultation à domicile" &&
                    missionLocations.new?.lat ? (
                    <DoctorAffectationMap
                      doctors={doctors.filter(
                        (d) =>
                          d.ville === newMissionData.ville &&
                          d.loc
                      )}
                      missionLocation={missionLocations.new}
                      selectedDoctorId={newMissionData.doctor_id}
                      onDoctorSelect={(doctorId) => {
                        setNewMissionData((prev) => ({
                          ...prev,
                          doctor_id: doctorId,
                        }));
                      }}
                    />
                  ) : newMissionData.type_mission === "Acte infirmier" &&
                    missionLocations.new?.lat ? (
                    <NurseAffectationMap
                      nurses={nurses.filter(
                        (n) =>
                          n.ville === newMissionData.ville &&
                          n.loc
                      )}
                      missionLocation={missionLocations.new}
                      selectedNurseId={newMissionData.nurse_id}
                      onNurseSelect={(nurseId) => {
                        setNewMissionData((prev) => ({
                          ...prev,
                          nurse_id: nurseId,
                        }));
                      }}
                    />
                  ) : (
                    <Box
                      p="40px"
                      textAlign="center"
                      bg="#FCFCFC"
                      borderRadius="6px"
                      border="1px solid"
                      borderColor="#E5E5E5"
                    >
                      <Text color="#656C7A" fontSize="14px">
                        {!missionLocations.new?.lat
                          ? "Veuillez d'abord sélectionner une adresse de mission."
                          : newMissionData.type_mission === "Consultation à domicile"
                            ? "Aucun médecin avec position disponible pour cette ville."
                            : newMissionData.type_mission === "Acte infirmier"
                              ? "Aucun infirmier avec position disponible pour cette ville."
                              : "L'affectation d'équipe est uniquement disponible pour les missions d'ambulance, de consultation à domicile ou d'acte infirmier."}
                      </Text>
                    </Box>
                  )}
                </Box>
              )}

              {/* Étape 3 : Patient (design Figma) */}
              {activeStep === 2 && !isTwoStepFlow && (
                <VStack spacing="14px" align="stretch">
                  <Box px="20px">
                    <Box bg="#F9F9F9" p="20px" borderRadius="12px">
                      <Text
                        fontFamily="Poppins, sans-serif"
                        fontSize="16px"
                        fontWeight="500"
                        lineHeight="1.2"
                        color="#000000"
                        mb="6px"
                      >
                        Patient
                      </Text>
                      <Text
                        fontSize="13px"
                        color="#656C7A"
                        lineHeight="1.4"
                        mb="14px"
                      >
                        L'IPP a été saisi à l'étape 1. Complétez ou modifiez le nom, prénom, âge, sexe ; le CIN est facultatif.
                      </Text>

                      <VStack spacing="14px" align="stretch">
                        {/* IPP (rappel / correction) */}
                        <FormControl isInvalid={!!invalidFields.new_patient_ipp}>
                          <FormLabel fontSize="13px" fontWeight="500" color="#000000" mb="10px">
                            IPP
                          </FormLabel>
                          <Input
                            placeholder="Saisissez IPP"
                            value={newMissionData.new_patient.ipp || ""}
                            onChange={(e) => handlePatientFieldChange("ipp", e.target.value)}
                            h="42px"
                            bg="white"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            color="#656C7A"
                          />
                        </FormControl>

                        {/* Nom * + Prénom * */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                          <FormControl isRequired isInvalid={!!invalidFields.new_patient_nom}>
                            <FormLabel fontSize="13px" fontWeight="500" color="#000000" mb="10px">
                              Nom
                            </FormLabel>
                            <Input
                              placeholder="Saisissez Nom"
                              value={newMissionData.new_patient.nom || ""}
                              onChange={(e) => handlePatientFieldChange("nom", e.target.value)}
                              h="42px"
                              bg="white"
                              borderRadius="6px"
                              border="1px solid"
                              borderColor="#E5E5E5"
                              boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                              fontSize="13px"
                              color="#656C7A"
                            />
                          </FormControl>

                          <FormControl isRequired isInvalid={!!invalidFields.new_patient_prenom}>
                            <FormLabel fontSize="13px" fontWeight="500" color="#000000" mb="10px">
                              Prénom
                            </FormLabel>
                            <Input
                              placeholder="Saisissez Prénom"
                              value={newMissionData.new_patient.prenom || ""}
                              onChange={(e) => handlePatientFieldChange("prenom", e.target.value)}
                              h="42px"
                              bg="white"
                              borderRadius="6px"
                              border="1px solid"
                              borderColor="#E5E5E5"
                              boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                              fontSize="13px"
                              color="#656C7A"
                            />
                          </FormControl>
                        </SimpleGrid>

                        {/* Âge * + Sexe * */}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                          <FormControl isRequired isInvalid={!!invalidFields.new_patient_age}>
                            <FormLabel fontSize="13px" fontWeight="500" color="#000000" mb="10px">
                              Âge
                            </FormLabel>
                            <Input
                              type="number"
                              placeholder="Saisissez Âge"
                              value={newMissionData.new_patient.age || ""}
                              onChange={(e) => handlePatientFieldChange("age", e.target.value)}
                              h="42px"
                              bg="white"
                              borderRadius="6px"
                              border="1px solid"
                              borderColor="#E5E5E5"
                              boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                              fontSize="13px"
                              color="#656C7A"
                            />
                          </FormControl>

                          <FormControl isRequired isInvalid={!!invalidFields.new_patient_sexe}>
                            <FormLabel fontSize="13px" fontWeight="500" color="#000000" mb="10px">
                              Sexe
                            </FormLabel>
                            <RadioGroup
                              value={normalizeSexeForDisplay(newMissionData.new_patient.sexe)}
                              onChange={(value) => handlePatientFieldChange("sexe", value)}
                            >
                              <HStack
                                spacing="24px"
                                py="10px"
                                px="14px"
                                border="1px solid"
                                borderColor={invalidFields.new_patient_sexe ? "#E53E3E" : "#E5E5E5"}
                                borderRadius="6px"
                                bg="white"
                                minH="42px"
                                align="center"
                              >
                                <Radio value="Homme" colorScheme="blue" size="md">
                                  <Text fontSize="13px" fontWeight="500" color="#000000">
                                    Homme
                                  </Text>
                                </Radio>
                                <Radio value="Femme" colorScheme="blue" size="md">
                                  <Text fontSize="13px" fontWeight="500" color="#000000">
                                    Femme
                                  </Text>
                                </Radio>
                              </HStack>
                            </RadioGroup>
                          </FormControl>
                        </SimpleGrid>

                        {/* CIN (facultatif) */}
                        <FormControl isInvalid={!!invalidFields.new_patient_cin}>
                          <FormLabel fontSize="13px" fontWeight="500" color="#000000" mb="10px">
                            CIN (facultatif)
                          </FormLabel>
                          <Input
                            placeholder="Saisissez le CIN du patient"
                            value={newMissionData.new_patient.cin || ""}
                            onChange={(e) => handlePatientFieldChange("cin", e.target.value)}
                            h="42px"
                            bg="white"
                            borderRadius="6px"
                            border="1px solid"
                            borderColor="#E5E5E5"
                            boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                            fontSize="13px"
                            color="#656C7A"
                          />
                        </FormControl>

                      </VStack>
                    </Box>
                  </Box>

                  {/* Bloc Spécialité infirmier supprimé (plus nécessaire) */}
                </VStack>
              )}

              {/* Boutons bas de page selon l'étape */}
              {activeStep < lastStepIndex && (
                <Flex justify="space-between" pt="8px">
                  <Button
                    variant="ghost"
                    leftIcon={<Icon as={MdArrowBack} />}
                    onClick={() => activeStep > 0 && setActiveStep(activeStep - 1)}
                    isDisabled={activeStep === 0}
                    opacity={activeStep === 0 ? 0.3 : 1}
                  >
                    Étape précédente
                  </Button>
                  <HStack spacing="10px">
                    <Button
                      variant="outline"
                      borderColor="gray.200"
                      onClick={() => navigate(`/admin/missions`)}
                    >
                      Annuler
                    </Button>
                    {/* Bouton "Confirmer et créer la mission" pour l'étape Affectation */}
                    {activeStep === 1 &&
                      newMissionData.ambulancier_id &&
                      !isTwoStepFlow && (
                        <Button
                          bg="#29BACD"
                          color="white"
                          _hover={{ bg: "#25a5b8" }}
                          rightIcon={<Icon as={MdArrowForward} />}
                          onClick={async () => {
                            if (!validateStep1()) return;
                            // Pour ambulance, créer une mission partielle
                            await handleCreatePartial();
                          }}
                          isLoading={isAdding}
                          px="12px"
                          py="11px"
                          h="auto"
                          fontSize="13px"
                          fontWeight="500"
                          borderRadius="6px"
                        >
                          Confirmer et créer la mission
                        </Button>
                      )}
                    {activeStep === 1 &&
                      newMissionData.ambulancier_id &&
                      isTwoStepFlow && (
                        <Button
                          bg="#006133"
                          color="white"
                          rightIcon={<Icon as={MdArrowForward} />}
                          onClick={async () => {
                            await handleCreate();
                          }}
                          isLoading={isAdding}
                          px="12px"
                          py="11px"
                          h="auto"
                          fontSize="13px"
                          fontWeight="500"
                          borderRadius="6px"
                        >
                          Créer la mission
                        </Button>
                      )}
                    {/* Bouton "Étape suivante" - masqué si on peut créer la mission directement */}
                    {!(activeStep === 1 &&
                      newMissionData.ambulancier_id) && (
                        <Button
                          bg="#006133"
                          color="white"
                          rightIcon={<Icon as={MdArrowForward} />}
                          onClick={() => {
                            let ok = false;
                            if (activeStep === 0) ok = validateStep1();
                            else if (activeStep === 1) {
                              // Validation pour l'étape d'affectation
                              if (newMissionData.type_mission === "ambulance" && !newMissionData.ambulancier_id) {
                                toast({
                                  title: "Ambulancier requis",
                                  description: "Veuillez sélectionner un ambulancier",
                                  status: "error",
                                  isClosable: true,
                                });
                                return;
                              }
                              ok = true;
                            }
                            if (ok && activeStep < lastStepIndex) setActiveStep(activeStep + 1);
                          }}
                        >
                          Étape suivante
                        </Button>
                      )}
                  </HStack>
                </Flex>
              )}

              {activeStep === 2 && !isTwoStepFlow && (
                <Flex justify="space-between" pt="8px">
                  <Button
                    variant="ghost"
                    leftIcon={<Icon as={MdArrowBack} />}
                    onClick={() => setActiveStep(1)}
                  >
                    Étape précédente
                  </Button>
                  <HStack spacing="10px">
                    <Button
                      variant="outline"
                      borderColor="gray.200"
                      onClick={() => navigate(`/admin/missions`)}
                    >
                      Annuler
                    </Button>
                    <Button
                      bg="#006133"
                      color="white"
                      rightIcon={<Icon as={MdArrowForward} />}
                      onClick={async () => {
                        if (!validateStep2()) return;
                        await handleCreate();
                      }}
                      isLoading={isAdding}
                    >
                      {partialMissionId ? "Mettre à jour la mission" : "Créer la mission"}
                    </Button>
                  </HStack>
                </Flex>
              )}

              {activeStep === lastStepIndex && isTwoStepFlow && (
                <Flex justify="space-between" pt="8px">
                  <Button
                    variant="ghost"
                    leftIcon={<Icon as={MdArrowBack} />}
                    onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                  >
                    Étape précédente
                  </Button>
                  <HStack spacing="10px">
                    <Button
                      variant="outline"
                      borderColor="gray.200"
                      onClick={() => navigate(`/admin/missions`)}
                    >
                      Annuler
                    </Button>
                    <Button
                      bg="#006133"
                      color="white"
                      rightIcon={<Icon as={MdArrowForward} />}
                      onClick={async () => {
                        await handleCreate();
                      }}
                      isLoading={isAdding}
                    >
                      Créer la mission
                    </Button>
                  </HStack>
                </Flex>
              )}
            </VStack>
          </Box>
        </Flex>
      )}

      {/* Modal pour ajouter rapidement un médecin lié à l'hôpital sélectionné */}
      <Modal
        isOpen={isAddDoctorModalOpen}
        onClose={() => {
          if (!creatingDoctor) {
            setIsAddDoctorModalOpen(false);
            setAddDoctorContext(null);
          }
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Ajouter un médecin</ModalHeader>
          <ModalCloseButton disabled={creatingDoctor} />
          <ModalBody pb={4}>
            <VStack spacing="10px" align="stretch">
              <FormControl isRequired>
                <FormLabel>Nom</FormLabel>
                <Input
                  value={newDoctorData.nom}
                  onChange={(e) =>
                    setNewDoctorData((prev) => ({ ...prev, nom: e.target.value }))
                  }
                  placeholder="Nom du médecin"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Prénom</FormLabel>
                <Input
                  value={newDoctorData.prenom}
                  onChange={(e) =>
                    setNewDoctorData((prev) => ({ ...prev, prenom: e.target.value }))
                  }
                  placeholder="Prénom du médecin"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Téléphone</FormLabel>
                <Input
                  value={newDoctorData.téléphone}
                  onChange={(e) =>
                    setNewDoctorData((prev) => ({ ...prev, téléphone: e.target.value }))
                  }
                  placeholder="Téléphone"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Ville</FormLabel>
                <Input
                  value={newDoctorData.ville}
                  onChange={(e) =>
                    setNewDoctorData((prev) => ({ ...prev, ville: e.target.value }))
                  }
                  placeholder="Ville"
                />
              </FormControl>
              <Text fontSize="12px" color="#656C7A">
                L&apos;email, le rôle, le statut, la date de naissance et la langue seront
                remplis automatiquement (email au format nomprenom@rsk.ma).
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              mr={3}
              onClick={() => {
                if (!creatingDoctor) {
                  setIsAddDoctorModalOpen(false);
                  setAddDoctorContext(null);
                }
              }}
            >
              Annuler
            </Button>
            <Button colorScheme="blue" onClick={handleCreateDoctor} isLoading={creatingDoctor}>
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const MissionCreate = () => {
  return (
    <GoogleMapsLoader>
      <MissionCreateContent />
    </GoogleMapsLoader>
  );
};

export default MissionCreate;

